import { useUserRole } from '@lib/useUserRole';
import { hasCustomTimeFeatureFor } from '@lib/featureChecks';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import buildProgressArcPath from '@/lib/progressArc';
import confetti, { type Options } from 'canvas-confetti';
import type { Toast } from 'react-hot-toast';
import LineBreathing from '../components/LineBreathing';
import {
  normalizeBreathingStyle,
  type BreathingStyle,
} from '@/types/breathing';
import SessionCompleteModal from '../components/SessionCompleteModal';
import BadgeUnlockedModal from '../components/BadgeUnlockedModal';
import { getMostPopularBackgroundVideoId, saveSession } from '../lib/sessions';
import toastLib, { showErrorToast } from '../lib/toast';
import {
  fetchUnnotifiedBadges,
  fetchUserStreak,
  markBadgesNotified,
  type UserBadgeWithDetails,
} from '../lib/badges';
import { useAuth } from '../lib/useAuth';
import { supabase } from '../lib/supabase';
import { logBreathingEvent } from '../lib/breathingEvents';
import {
  completeBreathingPageTimeEntry,
  generateBreathingPageTimeId,
  startBreathingPageTimeEntry,
  type BreathingPageTimeExitType,
} from '../lib/breathingPageTime';
import { listBreathingRatios } from '../lib/breathingRatio';
import { listBackgroundVideos } from '../lib/backgroundVideos';
import { listBreathingStyles } from '../lib/breathingStyles';
import type {
  BreathingRatio,
  BackgroundVideo,
  BreathingStyleRecord,
  MusicTrack,
} from '../lib/api';

type BreathingPhase = 'inhale' | 'hold1' | 'exhale' | 'hold2';

interface PhaseConfig {
  label: string;
  duration: number;
  scale: number;
  color: string;
}

interface PhaseSequenceItem extends PhaseConfig {
  key: BreathingPhase;
}

interface BreathingLocationState {
  backgroundVideo?: string;
  backgroundType?: 'video' | 'image' | 'color' | 'gradient';
  backgroundSource?: string;
  backgroundImageUrl?: string | null;
  backgroundColor?: string | null;
  backgroundSound?: string;
  voiceGuidance?: boolean;
  affirmations?: boolean;
  affirmationType?: string;
  affirmationMode?: 'regular' | 'subliminal';
  musicEnabled?: boolean;
  musicTrackId?: string | null;
  music_enabled?: boolean;
  music_track_id?: string | null;
  phaseChime?: boolean;
  introVoice?: boolean;
  playInBackground?: boolean;
  hideCycleCount?: boolean;
  hideTimeLeft?: boolean;
  affirmationWhisperMode?: boolean;
  duration?: number | string;
  ratio?: string;
  customRatioParts?: unknown;
  isCustomDuration?: boolean;
  breathingStyle?: BreathingStyle;
  presetName?: string;
  skipCustomTimeGate?: boolean;
  affirmationAudioUrl?: string;
}

type BreathingPresetRow = {
  id: string;
  name: string;
  slug: string;
  inhale: number;
  hold_full: number;
  exhale: number;
  hold_empty: number;
  default_duration: number;
  default_background: string | null;
  default_audio: boolean;
  auto_start: boolean;
  is_active: boolean;
  is_public: boolean;
};

const BACKGROUND_VIDEO_BUCKET = 'background_videos';

const FALLBACK_LOCAL_VIDEOS: Record<string, string> = {
  none: '',
  starfield: '/videos/starfield.mp4',
  fireplace: '/videos/fireplace.mp4',
  mountain: '/videos/mountain.mp4',
  forest: '/videos/forest.mp4',
  aurora: '/videos/aurora.mp4',
  candle: '/videos/candle.mp4',
  'sunset-field': '/videos/sunset-field.mp4',
  underwater: '/videos/underwater.mp4',
  ocean: '/videos/ocean-waves.mp4',
  'ocean-waves': '/videos/ocean-waves.mp4',
};

const normalizeVideoKey = (value: string) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

const keyFromFileName = (fileName: string) => {
  if (!fileName) return '';
  const base = fileName.split('/').pop() || fileName;
  return base.replace(/\.[^.]+$/, '');
};

const ensureBackgroundVideoStoragePath = (path: string) => {
  const normalized = String(path || '').replace(/^\/+/, '');
  if (!normalized) return normalized;
  if (normalized.includes('/')) return normalized;
  return `${BACKGROUND_VIDEO_BUCKET}/${normalized}`;
};

const getSupabasePublicUrlForPath = (path: string) => {
  const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || '').replace(
    /\/$/,
    ''
  );
  if (!supabaseUrl || !path) return '';
  return `${supabaseUrl}/storage/v1/object/public/${encodeURI(path)}`;
};

const BREATHING_PAGE_NAME = 'breathing';
const VISITOR_ID_STORAGE_KEY = 'xhalin_visitor_id';

type BreathingEventValue =
  | string
  | number
  | boolean
  | null
  | Record<string, unknown>;

const generateBreathingSessionId = (): string => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = Math.random() * 16;
    const value =
      char === 'x' ? Math.floor(rand) : (Math.floor(rand) & 0x3) | 0x8;
    return value.toString(16);
  });
};

const isValidUuid = (value: string | null): value is string => {
  if (!value) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value
  );
};

const getOrCreateVisitorId = (): string => {
  if (typeof window === 'undefined') {
    return generateBreathingSessionId();
  }

  try {
    const existing = window.localStorage.getItem(VISITOR_ID_STORAGE_KEY);
    if (isValidUuid(existing)) {
      return existing;
    }
    const next = generateBreathingSessionId();
    window.localStorage.setItem(VISITOR_ID_STORAGE_KEY, next);
    return next;
  } catch {
    return generateBreathingSessionId();
  }
};

const findBackgroundVideoByKey = (
  key: string,
  videos: BackgroundVideo[]
): BackgroundVideo | null => {
  if (!key || key === 'none') return null;

  const normalizedKey = normalizeVideoKey(key);

  return (
    videos.find((video) => {
      if (!video?.file_name) return false;
      if (video.id === key) return true;

      const base = keyFromFileName(video.file_name);
      const normalizedBase = normalizeVideoKey(base);
      if (base === key) return true;
      if (normalizedBase === normalizedKey) return true;
      if (normalizedBase.includes(normalizedKey)) return true;
      if (normalizedKey.includes(normalizedBase)) return true;

      const lowerKey = key.toLowerCase();
      if (String(video.file_name).toLowerCase().includes(lowerKey)) {
        return true;
      }

      const normalizedName = normalizeVideoKey(video.name || '');
      if (normalizedName && normalizedName === normalizedKey) return true;
      if (normalizedName && normalizedName.includes(normalizedKey)) return true;
      if (
        normalizedName &&
        normalizedKey &&
        normalizedKey.includes(normalizedName)
      )
        return true;

      return false;
    }) || null
  );
};

const buildBackgroundVideoSrc = (
  key: string,
  videos: BackgroundVideo[],
  match?: BackgroundVideo | null
): string => {
  if (!key || key === 'none') return '';

  const resolvedMatch = match ?? findBackgroundVideoByKey(key, videos);

  if (resolvedMatch?.file_name?.trim()) {
    const raw = resolvedMatch.file_name.trim();
    if (raw.startsWith('http')) return raw;
    const storagePath = ensureBackgroundVideoStoragePath(raw);
    // Prefer Supabase public URL when VITE_SUPABASE_URL is configured.
    // When it's missing we should NOT return a bare storage path (which
    // would cause the browser to try to load an invalid local path and
    // produce 404s). Instead, fall back to a local video map or return
    // empty string so the component doesn't render an invalid <video>.
    const supabaseUrl = getSupabasePublicUrlForPath(storagePath);
    if (supabaseUrl) return supabaseUrl;

    // Try local fallback by matching the file base name to known local files
    const localKey = normalizeVideoKey(keyFromFileName(raw));
    if (FALLBACK_LOCAL_VIDEOS[localKey]) return FALLBACK_LOCAL_VIDEOS[localKey];

    // Last resort: return the raw storage path (allows Supabase-stored files to work
    // even if VITE_SUPABASE_URL is somehow not set in runtime).
    // This restores pre-fix behavior so videos keep playing.
    return storagePath.startsWith('http') ? storagePath : `/${storagePath}`;
  }

  const fallbackFile = key.endsWith('.mp4') ? key : `${key}.mp4`;
  const fallbackPath = ensureBackgroundVideoStoragePath(fallbackFile);
  const fallbackUrl = getSupabasePublicUrlForPath(fallbackPath);
  if (fallbackUrl) return fallbackUrl;

  return FALLBACK_LOCAL_VIDEOS[key] || '';
};

const basePhaseVisuals: Record<
  BreathingPhase,
  Omit<PhaseConfig, 'duration'>
> = {
  inhale: {
    label: 'Inhale',
    scale: 1.5,
    color: 'from-teal-400 to-cyan-500',
  },
  hold1: {
    label: 'Hold',
    scale: 1.5,
    color: 'from-blue-400 to-teal-400',
  },
  exhale: {
    label: 'Exhale',
    scale: 1,
    color: 'from-cyan-500 to-blue-500',
  },
  hold2: {
    label: 'Hold',
    scale: 1,
    color: 'from-blue-500 to-indigo-500',
  },
};

const defaultPhaseDurations: Record<BreathingPhase, number> = {
  inhale: 4,
  hold1: 4,
  exhale: 4,
  hold2: 4,
};

const phaseKeyOrder: BreathingPhase[] = ['inhale', 'hold1', 'exhale', 'hold2'];
// Phase labels were previously defined here but are not used anymore.
const RATIO_FALLBACK_TUPLE: [number, number, number, number] = [
  defaultPhaseDurations.inhale,
  defaultPhaseDurations.hold1,
  defaultPhaseDurations.exhale,
  defaultPhaseDurations.hold2,
];
const PRESET_DURATION_MINUTES = new Set([1, 3, 5, 10]);

const parseRatioString = (ratio: string): number[] => {
  if (!ratio) return [];
  return ratio
    .split(/[:\\-xX]/)
    .map((part) => Number(part.trim()))
    .filter((value) => Number.isFinite(value) && value >= 0);
};

const clampRatioPart = (
  value: number | undefined,
  fallback: number
): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  const truncated = Math.trunc(value);
  if (truncated < 0) return 0;
  if (truncated > 60) return 60;
  return truncated;
};

const sanitizeRatioFromArray = (
  values: unknown
): [number, number, number, number] | null => {
  if (!Array.isArray(values)) return null;

  const sanitized = RATIO_FALLBACK_TUPLE.map((fallback, index) =>
    clampRatioPart(
      typeof values[index] === 'string' || typeof values[index] === 'number'
        ? Number(values[index])
        : undefined,
      fallback
    )
  ) as [number, number, number, number];

  if (sanitized.every((value) => value === 0)) {
    return RATIO_FALLBACK_TUPLE;
  }

  return sanitized;
};

interface PersistentSessionToastProps {
  message: string;
  toastId: string;
  onContinue: () => void;
  onGoToDashboard: () => void;
  primaryLabel?: string;
  onRestart?: () => void;
}

function PersistentSessionToast({
  message,
  toastId,
  onContinue,
  onGoToDashboard,
  primaryLabel = 'Replay Session',
  onRestart,
}: PersistentSessionToastProps) {
  const [isClosing, setIsClosing] = useState(false);

  const handleAction = useCallback(
    (callback: () => void) => {
      if (isClosing) return;
      setIsClosing(true);
      toastLib.dismiss(toastId);
      callback();
    },
    [isClosing, toastId]
  );

  const backdrop =
    typeof document !== 'undefined'
      ? createPortal(
          <div
            className={`fixed inset-0 bg-black/60 backdrop-blur-md transition-opacity duration-150 ${
              isClosing ? 'opacity-0' : 'opacity-100'
            }`}
            aria-hidden="true"
          />,
          document.body
        )
      : null;

  const modalPortal =
    typeof document !== 'undefined'
      ? createPortal(
          <div
            className={`fixed inset-0 z-[9999] flex items-center justify-center p-6 transition-all duration-150 ${
              isClosing ? 'pointer-events-none' : ''
            }`}
          >
            <div
              className={`w-full max-w-lg rounded-[32px] bg-[#cffbfe] p-6 shadow-[0_35px_60px_rgba(15,23,42,0.35)] ring-1 ring-white/60 backdrop-blur-xl transition-all duration-150 ${
                isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
              }`}
            >
              {/* Removed 'Paused' label per design */}
              <p className="mt-2 text-lg font-semibold text-gray-900">
                {message}
              </p>
              <p className="mt-2 text-sm text-gray-600">
                What would you like to do next?
              </p>
              <div className="mt-6 flex flex-col gap-3">
                <button
                  type="button"
                  className="w-full rounded-2xl bg-white px-5 py-3 text-sm font-semibold tracking-wide text-gray-900 shadow-lg shadow-slate-900/30 transition hover:bg-white/95 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200"
                  onClick={() => handleAction(onContinue)}
                >
                  {primaryLabel}
                </button>
                {onRestart && (
                  <button
                    type="button"
                    className="w-full rounded-2xl bg-white px-5 py-3 text-sm font-semibold tracking-wide text-gray-900 shadow-lg shadow-slate-900/30 transition hover:bg-white/95 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200"
                    onClick={() => handleAction(onRestart)}
                  >
                    Restart Session
                  </button>
                )}
                <button
                  type="button"
                  className="w-full rounded-2xl bg-white px-5 py-3 text-sm font-semibold tracking-wide text-gray-900 shadow-lg shadow-slate-900/30 transition hover:bg-white/95 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200"
                  onClick={() => handleAction(onGoToDashboard)}
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  // Add a global class when the modal is visible so we can blur main content
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const cls = 'session-toast-open';
    if (!toastId) return;
    document.body.classList.add(cls);
    return () => {
      document.body.classList.remove(cls);
    };
  }, [toastId]);

  return (
    <>
      {backdrop}
      {modalPortal ?? (
        <div
          className={`relative z-[9999] w-full max-w-lg rounded-[32px] bg-[#cffbfe] p-6 shadow-[0_35px_60px_rgba(15,23,42,0.35)] ring-1 ring-white/60 backdrop-blur-xl transition-all duration-150 sm:max-w-3xl ${
            isClosing
              ? 'pointer-events-none scale-95 opacity-0'
              : 'scale-100 opacity-100'
          }`}
        >
          {/* Removed 'Paused' label per design */}
          <p className="mt-2 text-lg font-semibold text-gray-900">{message}</p>
          <p className="mt-2 text-sm text-gray-600">
            What would you like to do next?
          </p>
          <div className="mt-6 flex flex-col gap-3">
            <button
              type="button"
              className="w-full rounded-2xl bg-white px-5 py-3 text-sm font-semibold tracking-wide text-gray-900 shadow-lg shadow-slate-900/30 transition hover:bg-white/95 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200"
              onClick={() => handleAction(onContinue)}
            >
              {primaryLabel}
            </button>
            {onRestart && (
              <button
                type="button"
                className="w-full rounded-2xl bg-white px-5 py-3 text-sm font-semibold tracking-wide text-gray-900 shadow-lg shadow-slate-900/30 transition hover:bg-white/95 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200"
                onClick={() => handleAction(onRestart)}
              >
                Restart Session
              </button>
            )}
            <button
              type="button"
              className="w-full rounded-2xl bg-slate-900/90 px-5 py-3 text-sm font-semibold tracking-wide text-white shadow-lg shadow-slate-900/30 transition hover:bg-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
              onClick={() => handleAction(onGoToDashboard)}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      )}
    </>
  );
}

const sanitizeRatioFromString = (
  ratio: string
): [number, number, number, number] => {
  const parsed = parseRatioString(ratio);
  const sanitized = RATIO_FALLBACK_TUPLE.map((fallback, index) =>
    clampRatioPart(parsed[index], fallback)
  ) as [number, number, number, number];

  if (sanitized.every((value) => value === 0)) {
    return RATIO_FALLBACK_TUPLE;
  }

  return sanitized;
};

const buildPhaseSequence = (values: readonly number[]): PhaseSequenceItem[] => {
  const mappedValues: Record<BreathingPhase, number> = {
    inhale: values[0] ?? defaultPhaseDurations.inhale,
    hold1: values[1] ?? 0,
    exhale: values[2] ?? defaultPhaseDurations.exhale,
    hold2: values[3] ?? 0,
  };

  const sequence: PhaseSequenceItem[] = phaseKeyOrder
    .map((key) => ({ key, seconds: mappedValues[key] }))
    .filter((entry) => entry.seconds > 0)
    .map((entry) => ({
      key: entry.key,
      duration: entry.seconds * 1000,
      ...basePhaseVisuals[entry.key],
    }));

  if (sequence.length > 0) {
    return sequence;
  }

  return [
    {
      key: 'inhale',
      duration: defaultPhaseDurations.inhale * 1000,
      ...basePhaseVisuals.inhale,
    },
    {
      key: 'exhale',
      duration: defaultPhaseDurations.exhale * 1000,
      ...basePhaseVisuals.exhale,
    },
  ];
};

const completionSoundBases = Array.from(
  { length: 11 },
  (_, idx) => `session_complete-${String(idx + 1).padStart(2, '0')}`
);
// Centralized audio base (Supabase public storage for audio files)
const AUDIO_BASE =
  'https://hsfbmpaclvbltdfaazpo.supabase.co/storage/v1/object/public/audio';

const completionSoundPaths = completionSoundBases.map(
  (name) => `${AUDIO_BASE}/${name}.mp3`
);

const AFFIRMATION_AUDIO_FILES = {
  panic_attack_relief: 'affirmation_panic_attack_relief.mp3',
  calm_anxiety: 'affirmation_calm_anxiety.mp3',
  confidence_selfworth: 'affirmation_confidence_selfworth.mp3',
  money_abundance: 'affirmation_money_abundance.mp3',
  love_relationships: 'affirmation_love_relationships.mp3',
  health_healing: 'affirmation_health_healing.mp3',
  focus_clarity: 'affirmation_focus_clarity.mp3',
  gratitude: 'affirmation_gratitude.mp3',
  sleep_deep_rest: 'affirmation_sleep_deep_rest.mp3',
} as const;

type AffirmationAudioType = keyof typeof AFFIRMATION_AUDIO_FILES;

const DEFAULT_AFFIRMATION_TYPE: AffirmationAudioType = 'panic_attack_relief';

const AFFIRMATION_LOOP_DELAY_MS = 2000;

const isAffirmationAudioType = (value: string): value is AffirmationAudioType =>
  Object.prototype.hasOwnProperty.call(AFFIRMATION_AUDIO_FILES, value);

const normalizeAffirmationType = (value: unknown): string => {
  if (typeof value !== 'string') return DEFAULT_AFFIRMATION_TYPE;
  const normalized = value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  if (!normalized) return DEFAULT_AFFIRMATION_TYPE;
  if (['regular', 'whisper', 'classic'].includes(normalized)) {
    return DEFAULT_AFFIRMATION_TYPE;
  }
  return normalized;
};

const buildAffirmationAudioSrc = (
  type: string,
  overrideUrl?: string
): string => {
  // If we have a direct override URL (e.g. from DB)
  if (overrideUrl) {
    if (/^https?:\/\//i.test(overrideUrl)) return overrideUrl;
    // If it's just a filename or relative path
    const path = overrideUrl.replace(/^\/+/, '');
    const encodedPath = path.split('/').map(encodeURIComponent).join('/');
    return `${AUDIO_BASE}/${encodedPath}`;
  }

  // Conventional fallback for hardcoded types
  const normalized = normalizeAffirmationType(type);
  if (isAffirmationAudioType(normalized)) {
    const relative = AFFIRMATION_AUDIO_FILES[normalized];
    if (relative) {
      if (/^https?:\/\//i.test(relative)) return relative;
      const path = relative.replace(/^\/+/, '');
      const encodedPath = path.split('/').map(encodeURIComponent).join('/');
      return `${AUDIO_BASE}/${encodedPath}`;
    }
  }

  // Dynamic fallback for any other type strings (e.g. from user-added affirmation types)
  const fallbackFile = `affirmation_${normalized}.mp3`;
  return `${AUDIO_BASE}/${fallbackFile}`;
};

// buildProgressArcPath moved to src/lib/progressArc.ts

function Breathing() {
  const { presetSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Auth + role are needed for URL preset access checks
  const { user, userFeatures, refreshUserFeatures } = useAuth();
  const isAuthenticated = Boolean(user);
  const { role } = useUserRole(user?.id ?? null);
  const isAdmin = role === 'admin';
  const hasCustomTimeFeature = hasCustomTimeFeatureFor(userFeatures, isAdmin);
  const ringDebug =
    new URLSearchParams(location.search).get('ringDebug') === '1';
  const rawLocState =
    (location && (location.state as BreathingLocationState)) || null;
  const visitorIdRef = useRef<string>('');
  const breathingSessionIdRef = useRef<string>('');
  const latestUserIdRef = useRef<string | null>(user?.id ?? null);
  const activePageTimeEntryRef = useRef<{
    id: string;
    enteredAtIso: string;
    startedAtMs: number;
  } | null>(null);
  const pageTimeInitializedRef = useRef(false);
  const pageTimeSessionClosedRef = useRef(false);
  const pageTimeStartPromiseRef = useRef<Promise<void> | null>(null);
  const pendingTabHiddenTimeoutRef = useRef<number | null>(null);
  const pageViewLoggedRef = useRef(false);
  const sessionStartedRef = useRef(false);
  const sessionCompletedRef = useRef(false);
  const hasStartedRef = useRef(false);
  const isActiveRef = useRef(false);
  const isIntroPlayingRef = useRef(false);
  const isMountedRef = useRef(true);
  const sessionRemainingRef = useRef<number>(0);

  if (!visitorIdRef.current) {
    visitorIdRef.current = getOrCreateVisitorId();
  }

  if (!breathingSessionIdRef.current) {
    breathingSessionIdRef.current = generateBreathingSessionId();
  }

  useEffect(() => {
    latestUserIdRef.current = user?.id ?? null;
  }, [user?.id]);

  const logBreathingInteraction = useCallback(
    (event: {
      eventType: string;
      eventCategory: string;
      eventLabel?: string | null;
      eventValue?: BreathingEventValue;
    }) => {
      logBreathingEvent({
        ...event,
        visitorId: visitorIdRef.current,
        sessionId: breathingSessionIdRef.current,
        userId: user?.id ?? null,
        page: BREATHING_PAGE_NAME,
      }).catch((error) => {
        if (import.meta.env.DEV) {
          console.error('Failed to log breathing event', error);
        }
      });
    },
    [user?.id]
  );

  const resolveNavigationExitType =
    useCallback((): BreathingPageTimeExitType => {
      if (
        typeof performance !== 'undefined' &&
        typeof performance.getEntriesByType === 'function'
      ) {
        const entries = performance.getEntriesByType(
          'navigation'
        ) as PerformanceNavigationTiming[];
        const latestEntry = entries?.[entries.length - 1];
        if (latestEntry && latestEntry.type === 'reload') {
          return 'refresh';
        }
      }

      return 'unload';
    }, []);

  const startPageTimeTracking = useCallback(() => {
    if (
      pageTimeSessionClosedRef.current ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return;
    }

    if (activePageTimeEntryRef.current) {
      return;
    }

    const entryId = generateBreathingPageTimeId();
    const now = new Date();
    const entry = {
      id: entryId,
      enteredAtIso: now.toISOString(),
      startedAtMs: now.getTime(),
    };

    activePageTimeEntryRef.current = entry;

    const promise = startBreathingPageTimeEntry({
      id: entryId,
      visitorId: visitorIdRef.current,
      sessionId: breathingSessionIdRef.current,
      userId: latestUserIdRef.current,
      page: BREATHING_PAGE_NAME,
      enteredAt: entry.enteredAtIso,
    }).catch((error) => {
      if (import.meta.env.DEV) {
        console.error('[breathing] failed to start page timing', error);
      }
    });

    pageTimeStartPromiseRef.current = promise;
    promise.finally(() => {
      if (pageTimeStartPromiseRef.current === promise) {
        pageTimeStartPromiseRef.current = null;
      }
    });
  }, []);

  const completePageTimeTracking = useCallback(
    (
      exitType: BreathingPageTimeExitType,
      options?: { allowRestart?: boolean }
    ) => {
      const entry = activePageTimeEntryRef.current;
      if (!entry) {
        if (!options?.allowRestart) {
          pageTimeSessionClosedRef.current = true;
        }
        return;
      }

      activePageTimeEntryRef.current = null;

      const finalize = async () => {
        if (pageTimeStartPromiseRef.current) {
          try {
            await pageTimeStartPromiseRef.current;
          } catch {
            // ignore start failures on completion
          }
        }

        const now = new Date();
        const durationMs = Math.max(0, now.getTime() - entry.startedAtMs);
        try {
          await completeBreathingPageTimeEntry({
            id: entry.id,
            visitorId: visitorIdRef.current,
            sessionId: breathingSessionIdRef.current,
            userId: latestUserIdRef.current,
            page: BREATHING_PAGE_NAME,
            enteredAt: entry.enteredAtIso,
            exitedAt: now.toISOString(),
            durationSeconds: durationMs / 1000,
            exitType,
          });
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error('[breathing] failed to complete page timing', error);
          }
        }
      };

      void finalize();

      if (!options?.allowRestart) {
        pageTimeSessionClosedRef.current = true;
      }
    },
    []
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    if (pageTimeInitializedRef.current) {
      return;
    }

    pageTimeInitializedRef.current = true;
    startPageTimeTracking();

    const clearPendingTabHiddenTimeout = () => {
      if (pendingTabHiddenTimeoutRef.current !== null) {
        window.clearTimeout(pendingTabHiddenTimeoutRef.current);
        pendingTabHiddenTimeoutRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (pageTimeSessionClosedRef.current) return;
        clearPendingTabHiddenTimeout();
        pendingTabHiddenTimeoutRef.current = window.setTimeout(() => {
          pendingTabHiddenTimeoutRef.current = null;
          if (!pageTimeSessionClosedRef.current) {
            completePageTimeTracking('tab_hidden', { allowRestart: true });
          }
        }, 150);
      } else if (document.visibilityState === 'visible') {
        clearPendingTabHiddenTimeout();
        if (
          !pageTimeSessionClosedRef.current &&
          !activePageTimeEntryRef.current
        ) {
          startPageTimeTracking();
        }
      }
    };

    const handleBeforeUnload = () => {
      clearPendingTabHiddenTimeout();
      completePageTimeTracking(resolveNavigationExitType(), {
        allowRestart: false,
      });
    };

    const handlePageHide = () => {
      clearPendingTabHiddenTimeout();
      completePageTimeTracking(resolveNavigationExitType(), {
        allowRestart: false,
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      clearPendingTabHiddenTimeout();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      completePageTimeTracking('route_change', { allowRestart: false });
    };
  }, [
    completePageTimeTracking,
    resolveNavigationExitType,
    startPageTimeTracking,
  ]);

  const [presetOverrides, setPresetOverrides] =
    useState<Partial<BreathingLocationState> | null>(null);
  const [presetMessage, setPresetMessage] = useState<string | null>(null);
  const [mostPopularBackgroundVideoId, setMostPopularBackgroundVideoId] =
    useState<string | null>(null);
  const isUrlPreset = Boolean(presetSlug); // true when route /breathing/:presetSlug is used (shareable preset)
  // The URL preset should not auto-start immediately. We surface a Start button
  // that kicks off an intro countdown instead of an automatic 'Get ready' auto-start.

  useEffect(() => {
    let cancelled = false;

    async function loadPreset(slug: string) {
      setPresetMessage(null);
      setPresetOverrides(null);

      const trimmed = String(slug || '')
        .trim()
        .toLowerCase();
      if (!trimmed) {
        setPresetMessage('Breathing preset not found. Choose one to begin.');
        return;
      }

      try {
        const { data, error } = await supabase
          .from('breathing_presets')
          .select(
            'id,name,slug,inhale,hold_full,exhale,hold_empty,default_duration,default_background,default_audio,auto_start,is_active,is_public'
          )
          .eq('slug', trimmed)
          .maybeSingle();

        if (cancelled) return;

        if (error || !data) {
          setPresetMessage('Breathing preset not found. Choose one to begin.');
          return;
        }

        const row = data as unknown as BreathingPresetRow;

        // If the preset exists but is not public, only allow admins to access it.
        // For non-admin users, show a not-found message (don't redirect).
        if (row.is_public === false && !isAdmin) {
          setPresetMessage('Breathing preset not found. Choose one to begin.');
          return;
        }

        // If the preset exists but is disabled, treat it as not found
        if (row.is_active === false) {
          setPresetMessage('Breathing preset not found. Choose one to begin.');
          return;
        }

        const inhale = Number(row.inhale);
        const holdFull = Number(row.hold_full);
        const exhale = Number(row.exhale);
        const holdEmpty = Number(row.hold_empty);

        const fallback = RATIO_FALLBACK_TUPLE;
        const ratio = [inhale, holdFull, exhale, holdEmpty]
          .map((value, idx) =>
            Number.isFinite(value)
              ? Math.max(0, Math.trunc(value))
              : fallback[idx]
          )
          .join('-');

        setPresetOverrides({
          presetName: row.name,
          ratio,
          duration:
            Number.isFinite(Number(row.default_duration)) &&
            Number(row.default_duration) > 0
              ? Math.round(Number(row.default_duration) / 60)
              : 5,
          backgroundVideo: row.default_background || undefined,
          backgroundSound: 'on',
          voiceGuidance: true,
          phaseChime: true,
          introVoice: true,
          skipCustomTimeGate: true,
        });

        // Don't auto-start sessions for shareable URL presets; they should
        // allow the user to press Start. For other contexts (like quick-start
        // flows), auto-start logic is handled elsewhere or via state.
      } catch (_err) {
        if (cancelled) return;
        setPresetMessage('Breathing preset not found. Choose one to begin.');
      }
    }

    if (!presetSlug) {
      setPresetMessage(null);
      setPresetOverrides(null);

      return () => {
        cancelled = true;
      };
    }

    loadPreset(presetSlug);

    return () => {
      cancelled = true;
    };
  }, [presetSlug, isAdmin]);

  const locState = useMemo<BreathingLocationState | null>(() => {
    if (!rawLocState && !presetOverrides) return null;
    return {
      ...(rawLocState ?? {}),
      ...(presetOverrides ?? {}),
    } as BreathingLocationState;
  }, [presetOverrides, rawLocState]);
  const ratioString =
    typeof locState?.ratio === 'string' && locState.ratio.trim().length > 0
      ? locState.ratio
      : '4-4-4-4';
  const breathingStyle = normalizeBreathingStyle(locState?.breathingStyle);

  const sanitizedRatioParts = useMemo<[number, number, number, number]>(() => {
    const fromArray = sanitizeRatioFromArray(locState?.customRatioParts);
    if (fromArray) return fromArray;
    return sanitizeRatioFromString(ratioString || '');
  }, [locState?.customRatioParts, ratioString]);

  const ratioSummaryString = useMemo(
    () => sanitizedRatioParts.join('-'),
    [sanitizedRatioParts]
  );

  const phaseSequence = useMemo(
    () => buildPhaseSequence(sanitizedRatioParts),
    [sanitizedRatioParts]
  );
  const linePhases = useMemo(
    () =>
      phaseSequence.map((phase) => ({
        key: phase.key,
        durationMs: phase.duration,
      })),
    [phaseSequence]
  );
  const firstPhase = phaseSequence[0] ?? {
    key: 'inhale' as BreathingPhase,
    duration: defaultPhaseDurations.inhale * 1000,
    ...basePhaseVisuals.inhale,
  };
  const initialPhaseSeconds = firstPhase.duration / 1000;

  const [isActive, setIsActive] = useState(false);
  const [isIntroPlaying, setIsIntroPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(initialPhaseSeconds);
  const [introTimeRemaining, setIntroTimeRemaining] = useState<number>(() =>
    locState?.introVoice ? 10 : 0
  );
  const timerRef = useRef<number | null>(null);
  const phaseTickRef = useRef<number | null>(null);
  const phaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const introCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const skipIntroAudioRef = useRef(false);
  const pendingReplayRef = useRef(false);
  const introAudioRef = useRef<HTMLAudioElement | null>(null);
  const introAudioPrimedRef = useRef(false); // true if we preloaded and unlocked audio via user gesture
  const phaseAudioRef = useRef<HTMLAudioElement | null>(null);
  const voiceAudioRef = useRef<HTMLAudioElement | null>(null);
  const backgroundAudioRef = useRef<HTMLAudioElement | null>(null);
  const musicAudioRef = useRef<HTMLAudioElement | null>(null);
  const musicAudioBlobUrlRef = useRef<string | null>(null);
  const loadedMusicTrackIdRef = useRef<string | null>(null);
  const affirmationAudioRef = useRef<HTMLAudioElement | null>(null);
  const affirmationAudioUrlRef = useRef<string | null>(null);
  const affirmationFetchAbortRef = useRef<AbortController | null>(null);
  const affirmationLoopTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const affirmationLoopHandlerRef = useRef<(() => void) | null>(null);
  const affirmationFadeIntervalRef = useRef<ReturnType<
    typeof setInterval
  > | null>(null);
  const completionAudioRef = useRef<HTMLAudioElement | null>(null);
  const backgroundVideoRef = useRef<HTMLVideoElement | null>(null);
  const circleContainerRef = useRef<HTMLDivElement | null>(null);
  const progressRingWrapperRef = useRef<HTMLDivElement | null>(null);
  const svgProgressRingRef = useRef<SVGSVGElement | null>(null);
  const progressRingCircleRef = useRef<SVGPathElement | null>(null);
  const frozenRingHeartAngleRef = useRef<number>(-Math.PI / 2);
  const frozenRingProgressRef = useRef<number>(0);
  const frozenRingDirectionRef = useRef<'inhale' | 'exhale'>('inhale');
  const ringDebugLastLogRef = useRef<number>(0);
  const lastPhaseIndexRef = useRef<number>(currentPhaseIndex);
  const completionCelebratedRef = useRef<boolean>(false);
  const handleToggleRef = useRef<() => void>(() => {});
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const [circleDebugStats, setCircleDebugStats] = useState<{
    container: {
      width: number;
      height: number;
      top: number;
      left: number;
      zIndex: string;
    };
    wrapper: {
      width: number;
      height: number;
      top: number;
      left: number;
      zIndex: string;
    };
    svg: {
      width: number;
      height: number;
      top: number;
      left: number;
      zIndex: string;
    };
  } | null>(null);
  const [progressCircleDebug, setProgressCircleDebug] = useState<{
    totalLength: number | null;
    strokeColor: string;
    strokeWidth: string;
    dashArray: string;
    dashOffset: string;
  } | null>(null);
  const [, setVideoDebug] = useState('video: initializing');
  const [, setAudioDebug] = useState('audio: idle');
  const backgroundColorValue = useMemo(() => {
    if (locState?.backgroundType !== 'color') return null;
    if (
      typeof locState?.backgroundSource === 'string' &&
      locState.backgroundSource.trim().length > 0
    ) {
      return locState.backgroundSource;
    }
    if (
      typeof locState?.backgroundColor === 'string' &&
      locState.backgroundColor.trim().length > 0
    ) {
      return locState.backgroundColor;
    }
    return null;
  }, [
    locState?.backgroundColor,
    locState?.backgroundSource,
    locState?.backgroundType,
  ]);
  const backgroundGradientValue = useMemo(() => {
    if (locState?.backgroundType !== 'gradient') return null;
    if (
      typeof locState?.backgroundSource === 'string' &&
      locState.backgroundSource.trim().length > 0
    ) {
      return locState.backgroundSource;
    }
    return null;
  }, [locState?.backgroundSource, locState?.backgroundType]);
  const backgroundImageValue = useMemo(() => {
    if (locState?.backgroundType !== 'image') return null;
    if (
      typeof locState?.backgroundImageUrl === 'string' &&
      locState.backgroundImageUrl.trim().length > 0
    ) {
      return locState.backgroundImageUrl;
    }
    if (
      typeof locState?.backgroundSource === 'string' &&
      locState.backgroundSource.trim().length > 0
    ) {
      return locState.backgroundSource;
    }
    return null;
  }, [
    locState?.backgroundImageUrl,
    locState?.backgroundSource,
    locState?.backgroundType,
  ]);
  const isColorBackground = Boolean(backgroundColorValue);
  const isGradientBackground = Boolean(backgroundGradientValue);
  const isImageBackground = Boolean(backgroundImageValue);
  const hasStaticBackground =
    isColorBackground || isGradientBackground || isImageBackground;
  const selectedVideoKey = useMemo<string>(() => {
    if (hasStaticBackground) return 'none';
    const candidate =
      typeof locState?.backgroundVideo === 'string'
        ? locState.backgroundVideo
        : null;

    return candidate && candidate.trim().length > 0 ? candidate : 'ocean';
  }, [hasStaticBackground, locState?.backgroundVideo]);
  const currentPhaseEntry = phaseSequence[currentPhaseIndex] ?? firstPhase;
  const currentPhase = currentPhaseEntry.key;
  const phaseConfig = currentPhaseEntry;
  const isHoldPhase = currentPhase === 'hold1' || currentPhase === 'hold2';
  const phaseCount = phaseSequence.length > 0 ? phaseSequence.length : 1;
  useEffect(() => {
    if (currentPhaseIndex >= phaseCount) {
      setCurrentPhaseIndex(0);
    }
  }, [currentPhaseIndex, phaseCount]);
  const backgroundSoundOn = locState?.backgroundSound === 'on';
  const musicEnabledOn =
    Boolean(locState?.musicEnabled) || Boolean(locState?.music_enabled);
  const musicTrackId =
    (locState?.musicTrackId ?? locState?.music_track_id ?? null) || null;
  const voiceGuidanceOn = Boolean(locState?.voiceGuidance);
  const affirmationsOn = Boolean(locState?.affirmations);
  const affirmationWhisperModeOn = Boolean(locState?.affirmationWhisperMode);
  const phaseChimeOn = Boolean(locState?.phaseChime);
  const introVoiceOn = Boolean(locState?.introVoice);
  const hideCycleCount = Boolean(locState?.hideCycleCount);
  const hideTimeLeft = Boolean(locState?.hideTimeLeft);
  const affirmationTypeKey = useMemo<string>(() => {
    if (!affirmationsOn) return DEFAULT_AFFIRMATION_TYPE;
    return normalizeAffirmationType(locState?.affirmationType);
  }, [affirmationsOn, locState?.affirmationType]);
  const affirmationAudioSrc = useMemo<string | null>(() => {
    if (!affirmationsOn) return null;
    const src = buildAffirmationAudioSrc(
      affirmationTypeKey,
      locState?.affirmationAudioUrl
    );
    return src || null;
  }, [affirmationsOn, affirmationTypeKey, locState?.affirmationAudioUrl]);

  useEffect(() => {
    if (!affirmationsOn) return;
    console.debug('[Breathing] affirmation audio selection', {
      affirmationTypeKey,
      affirmationAudioUrlOverride: locState?.affirmationAudioUrl ?? null,
      affirmationAudioSrc,
      whisperMode: affirmationWhisperModeOn,
    });
  }, [
    affirmationsOn,
    affirmationTypeKey,
    locState?.affirmationAudioUrl,
    affirmationAudioSrc,
    affirmationWhisperModeOn,
  ]);
  // Receive duration (minutes) from Dashboard navigation state; default to 5
  const durationMinutes = (() => {
    if (
      typeof locState?.duration === 'number' &&
      Number.isFinite(locState.duration)
    ) {
      return locState.duration;
    }
    if (
      typeof locState?.duration === 'string' &&
      locState.duration.trim().length > 0
    ) {
      const parsed = Number(locState.duration);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
    return 5;
  })();

  const sessionUsesCustomDuration =
    locState?.isCustomDuration === true ||
    (typeof durationMinutes === 'number' &&
      !PRESET_DURATION_MINUTES.has(durationMinutes));

  const [breathingRatiosFromDb, setBreathingRatiosFromDb] = useState<
    BreathingRatio[]
  >([]);
  const [backgroundVideosFromDb, setBackgroundVideosFromDb] = useState<
    BackgroundVideo[]
  >([]);
  const [breathingStylesFromDb, setBreathingStylesFromDb] = useState<
    BreathingStyleRecord[]
  >([]);
  const [musicTrackFromDb, setMusicTrackFromDb] = useState<MusicTrack | null>(
    null
  );

  const matchedBackgroundVideo = useMemo(
    () => findBackgroundVideoByKey(selectedVideoKey, backgroundVideosFromDb),
    [backgroundVideosFromDb, selectedVideoKey]
  );

  const selectedVideoSrc = useMemo(
    () =>
      buildBackgroundVideoSrc(
        selectedVideoKey,
        backgroundVideosFromDb,
        matchedBackgroundVideo
      ),
    [backgroundVideosFromDb, matchedBackgroundVideo, selectedVideoKey]
  );

  // Blob URL for videos with moov atom at end (need full download before playing)
  const [videoBlobUrl, setVideoBlobUrl] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState(false);

  // Download video fully and create blob URL to work around moov-at-end issue
  useEffect(() => {
    if (!selectedVideoSrc) {
      setVideoBlobUrl(null);
      setVideoLoading(false);
      return;
    }

    let cancelled = false;
    setVideoLoading(true);
    setVideoBlobUrl(null);

    fetch(selectedVideoSrc)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setVideoBlobUrl(url);
        setVideoLoading(false);
        console.log('Video blob created:', url, 'size:', blob.size);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to download video:', err);
        setVideoLoading(false);
      });

    return () => {
      cancelled = true;
      // Cleanup old blob URL
      setVideoBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [selectedVideoSrc]);

  const isLineStyle = breathingStyle === 'line';

  // Attempt to load breathing ratio and background video records so we can
  // save their DB ids with sessions.
  useEffect(() => {
    (async () => {
      try {
        const br = await listBreathingRatios();
        setBreathingRatiosFromDb(br || []);
      } catch (e) {
        // ignore — optional feature
      }

      try {
        const bvs = await listBackgroundVideos();
        setBackgroundVideosFromDb(bvs || []);
      } catch (e) {
        // ignore — optional feature
      }
      try {
        const bs = await listBreathingStyles();
        setBreathingStylesFromDb(bs || []);
      } catch (e) {
        // ignore — optional feature
      }
    })();
  }, []);

  // Fetch selected music track record (active only) on page load.
  // Fail silently: breathing session should continue even if this fails.
  useEffect(() => {
    let cancelled = false;

    if (!musicEnabledOn || !musicTrackId) {
      setMusicTrackFromDb(null);
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const { data, error } = await supabase
          .from('music_tracks')
          .select('*')
          .eq('id', musicTrackId)
          .eq('is_active', true)
          .maybeSingle();

        if (cancelled) return;
        if (error || !data) {
          setMusicTrackFromDb(null);
          return;
        }

        setMusicTrackFromDb(data as MusicTrack);
      } catch {
        if (cancelled) return;
        setMusicTrackFromDb(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [musicEnabledOn, musicTrackId]);

  useEffect(() => {
    let active = true;

    if (!presetSlug || backgroundVideosFromDb.length === 0) {
      setMostPopularBackgroundVideoId(null);
      return () => {
        active = false;
      };
    }

    (async () => {
      const ids = backgroundVideosFromDb.map((video) => video.id);
      const topId = await getMostPopularBackgroundVideoId(ids);
      if (!active) return;
      setMostPopularBackgroundVideoId(topId);
    })();

    return () => {
      active = false;
    };
  }, [presetSlug, backgroundVideosFromDb]);

  const mostPopularBackgroundVideoKey = useMemo(() => {
    if (!mostPopularBackgroundVideoId) return null;
    const found = backgroundVideosFromDb.find(
      (video) => video.id === mostPopularBackgroundVideoId
    );
    if (!found) return null;
    const key = keyFromFileName(found.file_name);
    if (!key || key === 'none') return null;
    return key;
  }, [backgroundVideosFromDb, mostPopularBackgroundVideoId]);

  useEffect(() => {
    // Only apply the most-popular background for URL presets when the
    // preset didn't specify a background in the DB. Do NOT override a
    // preset's explicit `default_background` value (including 'none').
    if (
      !presetSlug ||
      !mostPopularBackgroundVideoKey ||
      !presetOverrides ||
      // If the preset already has an explicit background value (could be
      // 'none' or some key), don't overwrite it with the most-popular.
      typeof presetOverrides.backgroundVideo !== 'undefined'
    ) {
      return;
    }

    setPresetOverrides((prev) => {
      if (!prev || typeof prev.backgroundVideo !== 'undefined') {
        return prev;
      }
      return {
        ...prev,
        backgroundVideo: mostPopularBackgroundVideoKey,
      };
    });
  }, [mostPopularBackgroundVideoKey, presetOverrides, presetSlug]);

  const matchedBreathingRatioId = useMemo<string | null>(() => {
    // If the navigation provided explicit custom parts, treat as "other" (no preset)
    if (locState?.customRatioParts) return null;
    // Try to match by exact ratio string first
    const byRatio = breathingRatiosFromDb.find((r) => r.ratio === ratioString);
    if (byRatio) return byRatio.id;

    // Fallback: match by normalized summary (e.g., sanitized parts)
    const normalized = ratioSummaryString;
    const byNormalized = breathingRatiosFromDb.find(
      (r) =>
        r.ratio &&
        r.ratio
          .split(/[:\\-xX]/)
          .map((p) => String(Number(p)))
          .join('-') === normalized
    );
    return byNormalized ? byNormalized.id : null;
  }, [
    breathingRatiosFromDb,
    ratioString,
    ratioSummaryString,
    locState?.customRatioParts,
  ]);

  const matchedBackgroundVideoId = matchedBackgroundVideo?.id ?? null;

  const matchedBreathingStyleId = useMemo<string | null>(() => {
    const normalized = normalizeBreathingStyle(breathingStyle);
    const found = breathingStylesFromDb.find(
      (s) => s.name?.trim().toLowerCase() === normalized
    );
    return found ? found.id : null;
  }, [breathingStylesFromDb, breathingStyle]);

  useEffect(() => {
    if (!selectedVideoSrc) {
      setVideoDebug(`video: no file for key=${selectedVideoKey}`);
    } else {
      setVideoDebug(`video: selected ${selectedVideoKey}`);
    }
  }, [selectedVideoKey, selectedVideoSrc]);

  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch (e) {
      // ignore
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!selectedVideoSrc) {
      setVideoDebug(`video: no source for key=${selectedVideoKey}`);
      setVideoEl(null);
    }
  }, [selectedVideoSrc, selectedVideoKey]);

  const phaseSeconds = phaseConfig.duration / 1000;
  const circleCircumference = 2 * Math.PI * 45;
  const phaseProgressRatio = phaseSeconds
    ? Math.min(1, Math.max(0, (phaseSeconds - timeRemaining) / phaseSeconds))
    : 0;
  const displayPhaseProgressRatio = isHoldPhase
    ? frozenRingProgressRef.current
    : phaseProgressRatio;
  const arcPhaseKey = isHoldPhase
    ? frozenRingDirectionRef.current
    : currentPhase === 'exhale'
      ? 'exhale'
      : 'inhale';
  useEffect(() => {
    if (!isHoldPhase) {
      frozenRingProgressRef.current = phaseProgressRatio;
      frozenRingDirectionRef.current =
        currentPhase === 'exhale' ? 'exhale' : 'inhale';
    }
  }, [currentPhase, isHoldPhase, phaseProgressRatio]);
  const progressArcPath = useMemo(
    () =>
      buildProgressArcPath(
        displayPhaseProgressRatio,
        45,
        arcPhaseKey === 'exhale'
      ),
    [displayPhaseProgressRatio, arcPhaseKey]
  );
  const phaseGradientClass =
    basePhaseVisuals[currentPhase]?.color || basePhaseVisuals.inhale.color;
  const phaseStrokeOffset =
    circleCircumference * (1 - displayPhaseProgressRatio);
  const displaySeconds = isActive
    ? Math.ceil(timeRemaining)
    : Math.max(0, Math.ceil(introTimeRemaining));
  const showReadyCountdown = !isActive && introVoiceOn;

  useEffect(() => {
    if (isActive || isIntroPlaying) return;
    setIntroTimeRemaining(locState?.introVoice ? 10 : 0);
  }, [locState?.introVoice, isActive, isIntroPlaying]);

  // Compute number of seconds per cycle (sum of all phase durations)
  const cycleDurationMs = phaseSequence.reduce(
    (acc, phase) => acc + phase.duration,
    0
  );
  const cycleSeconds =
    cycleDurationMs > 0
      ? cycleDurationMs / 1000
      : defaultPhaseDurations.inhale + defaultPhaseDurations.exhale;

  useEffect(() => {
    if (!ringDebug || isLineStyle) {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }

    let rafId: number | null = null;
    let rafIntervalId: number | null = null;

    const round = (value: number) => Number(value.toFixed(2));
    const measure = () => {
      const containerEl = circleContainerRef.current;
      const wrapperEl = progressRingWrapperRef.current;
      const svgEl = svgProgressRingRef.current;
      const progressCircleEl = progressRingCircleRef.current;
      if (!containerEl || !wrapperEl || !svgEl) return;
      const containerRect = containerEl.getBoundingClientRect();
      const wrapperRect = wrapperEl.getBoundingClientRect();
      const svgRect = svgEl.getBoundingClientRect();
      const containerStyles = window.getComputedStyle(containerEl);
      const wrapperStyles = window.getComputedStyle(wrapperEl);
      const svgStyles = window.getComputedStyle(svgEl);
      setCircleDebugStats({
        container: {
          width: round(containerRect.width),
          height: round(containerRect.height),
          top: round(containerRect.top),
          left: round(containerRect.left),
          zIndex: containerStyles.zIndex || 'auto',
        },
        wrapper: {
          width: round(wrapperRect.width),
          height: round(wrapperRect.height),
          top: round(wrapperRect.top),
          left: round(wrapperRect.left),
          zIndex: wrapperStyles.zIndex || 'auto',
        },
        svg: {
          width: round(svgRect.width),
          height: round(svgRect.height),
          top: round(svgRect.top),
          left: round(svgRect.left),
          zIndex: svgStyles.zIndex || 'auto',
        },
      });
      if (progressCircleEl) {
        let totalLength: number | null = null;
        try {
          totalLength = progressCircleEl.getTotalLength();
        } catch {
          totalLength = null;
        }
        const circleStyles = window.getComputedStyle(progressCircleEl);
        setProgressCircleDebug({
          totalLength: totalLength !== null ? round(totalLength) : null,
          strokeColor:
            circleStyles.stroke ||
            progressCircleEl.getAttribute('stroke') ||
            '',
          strokeWidth:
            circleStyles.strokeWidth ||
            progressCircleEl.getAttribute('stroke-width') ||
            '',
          dashArray:
            circleStyles.strokeDasharray ||
            progressCircleEl.getAttribute('stroke-dasharray') ||
            '',
          dashOffset:
            circleStyles.strokeDashoffset ||
            progressCircleEl.getAttribute('stroke-dashoffset') ||
            '',
        });
      } else {
        setProgressCircleDebug(null);
      }
    };

    const scheduleMeasure = () => {
      rafId = window.requestAnimationFrame(measure);
    };

    scheduleMeasure();
    rafIntervalId = window.setInterval(scheduleMeasure, 750);
    window.addEventListener('resize', scheduleMeasure);

    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      if (rafIntervalId !== null) {
        window.clearInterval(rafIntervalId);
      }
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [ringDebug, isLineStyle]);

  useEffect(() => {
    if (!ringDebug || !circleDebugStats) return;
    const now = Date.now();
    const LOG_INTERVAL_MS = 2000;
    if (now - ringDebugLastLogRef.current < LOG_INTERVAL_MS) {
      return;
    }
    ringDebugLastLogRef.current = now;
    try {
      console.groupCollapsed(
        '[ringDebug] circle layout',
        new Date().toISOString()
      );
      console.table({
        container: circleDebugStats.container,
        wrapper: circleDebugStats.wrapper,
        svg: circleDebugStats.svg,
      });
      if (progressCircleDebug) {
        console.table({ progressCircle: progressCircleDebug });
      } else {
        console.log('progressCircleDebug: null');
      }
      console.log('phaseStrokeOffset', phaseStrokeOffset);
      console.log('circleCircumference', circleCircumference);
      console.groupEnd();
    } catch (error) {
      console.warn('ringDebug logging failed', error);
    }
  }, [
    ringDebug,
    circleDebugStats,
    progressCircleDebug,
    phaseStrokeOffset,
    circleCircumference,
  ]);

  const totalCycles = Math.max(
    1,
    Math.ceil((durationMinutes * 60) / cycleSeconds)
  );
  const totalSessionSeconds = Math.max(
    0,
    Math.round(totalCycles * cycleSeconds)
  );

  const buildConfigUsedValue = useCallback(
    () => ({
      breathing_style: breathingStyle,
      inhale: sanitizedRatioParts[0] ?? defaultPhaseDurations.inhale,
      hold_full: sanitizedRatioParts[1] ?? defaultPhaseDurations.hold1,
      exhale: sanitizedRatioParts[2] ?? defaultPhaseDurations.exhale,
      hold_empty: sanitizedRatioParts[3] ?? defaultPhaseDurations.hold2,
      duration_seconds: totalSessionSeconds,
      background_video: selectedVideoKey,
      audio_enabled: backgroundSoundOn,
      affirmations_enabled: affirmationsOn,
      affirmation_type: affirmationsOn ? affirmationTypeKey : null,
    }),
    [
      affirmationsOn,
      affirmationTypeKey,
      backgroundSoundOn,
      breathingStyle,
      sanitizedRatioParts,
      selectedVideoKey,
      totalSessionSeconds,
    ]
  );

  // Track how many full breathing cycles have been completed
  const [completedCycles, setCompletedCycles] = useState<number>(0);

  // Time remaining for the whole session (seconds)
  const [sessionRemaining, setSessionRemaining] =
    useState<number>(totalSessionSeconds);
  const sessionIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  useEffect(() => {
    if (pageViewLoggedRef.current) return;
    pageViewLoggedRef.current = true;
    logBreathingInteraction({
      eventType: 'page_view',
      eventCategory: 'lifecycle',
    });
  }, [logBreathingInteraction]);

  useEffect(() => {
    hasStartedRef.current = hasStarted;
  }, [hasStarted]);

  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  useEffect(() => {
    isIntroPlayingRef.current = isIntroPlaying;
  }, [isIntroPlaying]);

  useEffect(() => {
    sessionRemainingRef.current = sessionRemaining;
  }, [sessionRemaining]);

  useEffect(() => {
    if (!isActive || !hasStarted) return;
    if (sessionStartedRef.current) return;
    sessionStartedRef.current = true;
    sessionCompletedRef.current = false;
    logBreathingInteraction({
      eventType: 'session_start',
      eventCategory: 'lifecycle',
    });
    logBreathingInteraction({
      eventType: 'config_used',
      eventCategory: 'breathing',
      eventValue: buildConfigUsedValue(),
    });
  }, [buildConfigUsedValue, hasStarted, isActive, logBreathingInteraction]);

  // Session complete modal and public save eligibility (30-second public save window)
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [publicSaveTimeRemaining, setPublicSaveTimeRemaining] = useState(30);
  const [isPublicSaveExpired, setIsPublicSaveExpired] = useState(false);
  const publicSaveTimerRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  // Badge unlocked modal
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [controlsFaded, setControlsFaded] = useState(false);
  const [unlockedBadges, setUnlockedBadges] = useState<UserBadgeWithDetails[]>(
    []
  );

  // (useAuth/useUserRole already declared above)

  // Reset counters and timers when totalCycles or duration changes
  useEffect(() => {
    setCompletedCycles(0);
    setSessionRemaining(totalSessionSeconds);
    setTimeRemaining(firstPhase.duration / 1000);
    lastPhaseIndexRef.current = 0;
    setCurrentPhaseIndex(0);
    completionCelebratedRef.current = false;
  }, [
    totalCycles,
    durationMinutes,
    totalSessionSeconds,
    phaseSequence,
    firstPhase.duration,
  ]);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;

      // Ensure affirmation audio/loop is fully stopped on navigation/unmount
      if (affirmationFetchAbortRef.current) {
        try {
          affirmationFetchAbortRef.current.abort();
        } catch (e) {
          /* ignore */
        }
        affirmationFetchAbortRef.current = null;
      }
      if (affirmationLoopTimeoutRef.current !== null) {
        clearTimeout(affirmationLoopTimeoutRef.current);
        affirmationLoopTimeoutRef.current = null;
      }
      if (affirmationFadeIntervalRef.current !== null) {
        clearInterval(affirmationFadeIntervalRef.current);
        affirmationFadeIntervalRef.current = null;
      }
      if (affirmationLoopHandlerRef.current && affirmationAudioRef.current) {
        affirmationAudioRef.current.removeEventListener(
          'ended',
          affirmationLoopHandlerRef.current
        );
      }
      affirmationLoopHandlerRef.current = null;
      if (affirmationAudioRef.current) {
        affirmationAudioRef.current.pause();
        try {
          if (affirmationAudioRef.current.src) {
            affirmationAudioRef.current.src = '';
            affirmationAudioRef.current.load();
          } else {
            affirmationAudioRef.current.removeAttribute('src');
          }
        } catch (e) {
          /* ignore */
        }
        affirmationAudioRef.current = null;
      }
      if (affirmationAudioUrlRef.current) {
        try {
          URL.revokeObjectURL(affirmationAudioUrlRef.current);
        } catch (e) {
          /* ignore */
        }
        affirmationAudioUrlRef.current = null;
      }

      if (introAudioRef.current) {
        introAudioRef.current.pause();
        try {
          if (introAudioRef.current.src) {
            introAudioRef.current.src = '';
            introAudioRef.current.load();
          } else {
            introAudioRef.current.removeAttribute('src');
          }
        } catch (e) {
          /* ignore */
        }
        introAudioRef.current = null;
      }
      if (phaseAudioRef.current) {
        phaseAudioRef.current.pause();
        try {
          if (phaseAudioRef.current.src) {
            phaseAudioRef.current.src = '';
            phaseAudioRef.current.load();
          } else {
            phaseAudioRef.current.removeAttribute('src');
          }
        } catch (e) {
          /* ignore */
        }
        phaseAudioRef.current = null;
      }
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause();
        try {
          if (voiceAudioRef.current.src) {
            voiceAudioRef.current.src = '';
            voiceAudioRef.current.load();
          } else {
            voiceAudioRef.current.removeAttribute('src');
          }
        } catch (e) {
          /* ignore */
        }
        voiceAudioRef.current = null;
      }
      if (backgroundAudioRef.current) {
        backgroundAudioRef.current.pause();
        try {
          if (backgroundAudioRef.current.src) {
            backgroundAudioRef.current.src = '';
            backgroundAudioRef.current.load();
          } else {
            backgroundAudioRef.current.removeAttribute('src');
          }
        } catch (e) {
          /* ignore */
        }
        backgroundAudioRef.current = null;
      }

      if (musicAudioRef.current) {
        musicAudioRef.current.pause();
        try {
          if (musicAudioRef.current.src) {
            musicAudioRef.current.src = '';
            musicAudioRef.current.load();
          } else {
            musicAudioRef.current.removeAttribute('src');
          }
        } catch (e) {
          /* ignore */
        }
        musicAudioRef.current = null;
      }
      if (musicAudioBlobUrlRef.current) {
        try {
          URL.revokeObjectURL(musicAudioBlobUrlRef.current);
        } catch (e) {
          /* ignore */
        }
        musicAudioBlobUrlRef.current = null;
      }
      loadedMusicTrackIdRef.current = null;
      if (completionAudioRef.current) {
        completionAudioRef.current.pause();
        try {
          if (completionAudioRef.current.src) {
            completionAudioRef.current.src = '';
            completionAudioRef.current.load();
          } else {
            completionAudioRef.current.removeAttribute('src');
          }
        } catch (e) {
          /* ignore */
        }
        completionAudioRef.current = null;
      }
      if (introCountdownRef.current) {
        clearInterval(introCountdownRef.current);
        introCountdownRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!isActive) return;

    if (phaseAudioRef.current) {
      phaseAudioRef.current.pause();
      try {
        if (phaseAudioRef.current.src) {
          phaseAudioRef.current.src = '';
          phaseAudioRef.current.load();
        } else {
          phaseAudioRef.current.removeAttribute('src');
        }
      } catch (e) {
        /* ignore */
      }
      phaseAudioRef.current = null;
    }

    if (voiceAudioRef.current) {
      voiceAudioRef.current.pause();
      try {
        if (voiceAudioRef.current.src) {
          voiceAudioRef.current.src = '';
          voiceAudioRef.current.load();
        } else {
          voiceAudioRef.current.removeAttribute('src');
        }
      } catch (e) {
        /* ignore */
      }
      voiceAudioRef.current = null;
    }

    let audioPath = '';
    let voicePath = '';
    if (currentPhase === 'inhale') {
      audioPath = `${AUDIO_BASE}/inhale.mp3`;
      voicePath = `${AUDIO_BASE}/inhale-voice.mp3`;
    } else if (currentPhase === 'exhale') {
      audioPath = `${AUDIO_BASE}/exhale.mp3`;
      voicePath = `${AUDIO_BASE}/exhale-voice.mp3`;
    } else if (currentPhase === 'hold1' || currentPhase === 'hold2') {
      voicePath = `${AUDIO_BASE}/hold-voice.mp3`;
    }

    // Only play phase chime sounds if Phase Chime is enabled
    if (audioPath) {
      if (phaseChimeOn) {
        // Download as blob to work around streaming issues
        fetch(audioPath)
          .then((res) =>
            res.ok ? res.blob() : Promise.reject(`HTTP ${res.status}`)
          )
          .then((blob) => {
            const blobUrl = URL.createObjectURL(blob);
            const audio = new Audio(blobUrl);
            audio.volume = 0.1;
            phaseAudioRef.current = audio;
            return audio.play();
          })
          .catch((err) => {
            console.warn('Phase audio failed:', audioPath, err);
          });
      } else {
        setAudioDebug(`audio: phase chime disabled -> ${audioPath} muted`);
      }
    }

    // Only play voice guidance if Voice Guidance is enabled
    if (voicePath) {
      if (voiceGuidanceOn) {
        // Download as blob to work around streaming issues
        fetch(voicePath)
          .then((res) =>
            res.ok ? res.blob() : Promise.reject(`HTTP ${res.status}`)
          )
          .then((blob) => {
            const blobUrl = URL.createObjectURL(blob);
            const v = new Audio(blobUrl);
            voiceAudioRef.current = v;
            return v.play();
          })
          .catch((err) => {
            console.warn('Voice audio failed:', voicePath, err);
          });
      } else {
        setAudioDebug(`audio: voice guidance disabled -> ${voicePath} muted`);
      }
    }
  }, [isActive, currentPhase, phaseChimeOn, voiceGuidanceOn]);

  useEffect(() => {
    if (isActive) {
      phaseTickRef.current = performance.now();

      const tick = (now: number) => {
        const last = phaseTickRef.current ?? now;
        phaseTickRef.current = now;
        const deltaSeconds = (now - last) / 1000;

        setTimeRemaining((prev) => {
          const next = prev - deltaSeconds;
          return next <= 0 ? 0 : next;
        });

        timerRef.current = requestAnimationFrame(tick);
      };

      timerRef.current = requestAnimationFrame(tick);

      phaseTimerRef.current = setTimeout(() => {
        setCurrentPhaseIndex((prev) => (prev + 1) % phaseCount);
      }, phaseConfig.duration);

      return () => {
        if (timerRef.current !== null) {
          cancelAnimationFrame(timerRef.current);
          timerRef.current = null;
        }
        if (phaseTimerRef.current) clearTimeout(phaseTimerRef.current);
      };
    }

    if (timerRef.current !== null) {
      cancelAnimationFrame(timerRef.current);
      timerRef.current = null;
    }
    if (phaseTimerRef.current) clearTimeout(phaseTimerRef.current);
    phaseTickRef.current = null;
    return undefined;
  }, [isActive, currentPhaseIndex, phaseConfig.duration, phaseCount]);

  useEffect(() => {
    if (!isActive) return;
    if (lastPhaseIndexRef.current === currentPhaseIndex) return;
    lastPhaseIndexRef.current = currentPhaseIndex;
    setTimeRemaining(phaseConfig.duration / 1000);
  }, [currentPhaseIndex, phaseConfig.duration, isActive]);

  // Track completed cycles: when phase index wraps from last -> 0
  const prevPhaseIndex = useRef<number>(currentPhaseIndex);
  useEffect(() => {
    if (!hasStarted) {
      prevPhaseIndex.current = currentPhaseIndex;
      return;
    }

    const lastIndex = phaseCount - 1;
    if (prevPhaseIndex.current === lastIndex && currentPhaseIndex === 0) {
      setCompletedCycles((c) => Math.min(c + 1, totalCycles));
    }

    prevPhaseIndex.current = currentPhaseIndex;
  }, [currentPhaseIndex, hasStarted, totalCycles, phaseCount]);

  // Session remaining countdown (mm:ss). Start/stop when isActive toggles.
  useEffect(() => {
    if (isActive) {
      if (sessionIntervalRef.current) clearInterval(sessionIntervalRef.current);
      sessionIntervalRef.current = setInterval(() => {
        setSessionRemaining((s) => {
          if (s <= 1) {
            if (sessionIntervalRef.current)
              clearInterval(sessionIntervalRef.current);
            sessionIntervalRef.current = null;
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    } else {
      if (sessionIntervalRef.current) {
        clearInterval(sessionIntervalRef.current);
        sessionIntervalRef.current = null;
      }
    }

    return () => {
      if (sessionIntervalRef.current) {
        clearInterval(sessionIntervalRef.current);
        sessionIntervalRef.current = null;
      }
    };
  }, [isActive]);

  const ensureBackgroundAudio = useCallback(() => {
    if (!backgroundSoundOn) {
      setAudioDebug('audio: backgroundSound is off');
      return;
    }

    if (backgroundAudioRef.current) {
      const bgPlay = backgroundAudioRef.current.play();
      if (bgPlay && typeof bgPlay.then === 'function') {
        bgPlay.catch(() => {});
      }
      return;
    }

    try {
      let mp3Path = `${AUDIO_BASE}/ocean-waves.mp3`;
      if (selectedVideoSrc) {
        // If selectedVideoSrc is a local /videos/ path, replace with AUDIO_BASE.
        if (selectedVideoSrc.startsWith('/videos/')) {
          const file = selectedVideoSrc.split('/').pop() || 'ocean-waves.mp3';
          mp3Path = `${AUDIO_BASE}/${file.replace(/\.mp4$/i, '.mp3')}`;
        } else {
          // For full URLs (e.g., Supabase storage links) swap video dirs to audio
          mp3Path = selectedVideoSrc
            .replace(/\/(?:background_videos|videos)\//, '/audio/')
            .replace(/\.mp4$/i, '.mp3');
        }
      }

      setAudioDebug(`audio: checking ${mp3Path}`);
      (async () => {
        // Download the full audio file as blob to work around moov-at-end issue
        try {
          setAudioDebug(`audio: downloading ${mp3Path}`);
          const res = await fetch(mp3Path);
          if (!res.ok) {
            setAudioDebug(`audio: fetch ${mp3Path} returned ${res.status}`);
            return;
          }

          if (!isMountedRef.current) return;

          const blob = await res.blob();
          if (!isMountedRef.current) return;

          const blobUrl = URL.createObjectURL(blob);
          setAudioDebug(
            `audio: blob created for ${mp3Path} (${blob.size} bytes)`
          );

          if (
            !isMountedRef.current ||
            (!isActiveRef.current && !isIntroPlayingRef.current)
          ) {
            URL.revokeObjectURL(blobUrl);
            return;
          }

          const bg = new Audio(blobUrl);
          bg.loop = true;
          bg.volume = 0.2;
          const onPlay = () => {
            setAudioDebug(
              `audio: playing ${mp3Path} t=${bg.currentTime.toFixed(2)}`
            );
            console.debug('background audio play', mp3Path, {
              currentTime: bg.currentTime,
              volume: bg.volume,
            });
          };
          const onCanPlay = () => {
            setAudioDebug(`audio: canplay ${mp3Path}`);
            console.debug('background audio canplay', mp3Path);
          };
          const onError = (ev: Event) => {
            const err = (ev.target as HTMLMediaElement).error;
            setAudioDebug(
              `audio: error ${mp3Path} code=${err?.code || 'unknown'}`
            );
            console.error('background audio error', mp3Path, err);
          };
          const onPause = () => {
            setAudioDebug(
              `audio: paused ${mp3Path} t=${bg.currentTime.toFixed(2)}`
            );
          };

          bg.addEventListener('play', onPlay);
          bg.addEventListener('canplay', onCanPlay);
          bg.addEventListener('error', onError);
          bg.addEventListener('pause', onPause);

          backgroundAudioRef.current = bg;
          const bgPlay = bg.play();
          if (bgPlay && typeof bgPlay.then === 'function') {
            bgPlay
              .then(() => {
                setAudioDebug(`audio: play() resolved for ${mp3Path}`);
              })
              .catch((err) => {
                setAudioDebug(`audio: play() rejected for ${mp3Path}`);
                console.warn('background audio play() rejected', mp3Path, err);
              });
          } else {
            setAudioDebug(`audio: play() invoked (no promise) ${mp3Path}`);
          }
        } catch (e) {
          setAudioDebug(`audio: download/create failed for ${mp3Path}`);
          console.error('Failed to create background audio', e);
        }
      })();
    } catch (e) {
      setAudioDebug('audio: unexpected error initializing background audio');
      console.error(e);
    }
  }, [backgroundSoundOn, selectedVideoSrc]);

  const ensureMusicAudio = useCallback(() => {
    if (!musicEnabledOn || !musicTrackId) return;
    if (!musicTrackFromDb?.audio_url) return;
    if (musicTrackFromDb.is_active !== true) return;

    // If the selected track changed, dispose the previous audio.
    if (
      loadedMusicTrackIdRef.current &&
      loadedMusicTrackIdRef.current !== musicTrackId
    ) {
      if (musicAudioRef.current) {
        try {
          musicAudioRef.current.pause();
        } catch {
          // ignore
        }
        try {
          if (musicAudioRef.current.src) {
            musicAudioRef.current.src = '';
            musicAudioRef.current.load();
          } else {
            musicAudioRef.current.removeAttribute('src');
          }
        } catch {
          // ignore
        }
        musicAudioRef.current = null;
      }
      if (musicAudioBlobUrlRef.current) {
        try {
          URL.revokeObjectURL(musicAudioBlobUrlRef.current);
        } catch {
          // ignore
        }
        musicAudioBlobUrlRef.current = null;
      }
      loadedMusicTrackIdRef.current = null;
    }

    // Resume if already created.
    if (musicAudioRef.current) {
      const p = musicAudioRef.current.play();
      if (p && typeof p.then === 'function') {
        p.catch(() => {});
      }
      return;
    }

    // Create looped music once per session; fail silently.
    (async () => {
      try {
        const res = await fetch(musicTrackFromDb.audio_url);
        if (!res.ok) return;

        if (!isMountedRef.current) return;

        const blob = await res.blob();
        if (!isMountedRef.current) return;

        const blobUrl = URL.createObjectURL(blob);

        if (musicAudioBlobUrlRef.current) {
          try {
            URL.revokeObjectURL(musicAudioBlobUrlRef.current);
          } catch {
            // ignore
          }
        }
        musicAudioBlobUrlRef.current = blobUrl;

        const audio = new Audio(blobUrl);
        audio.loop = true;
        // Default music volume ~25–35%, and kept independent of other audio.
        audio.volume = 0.28;

        if (
          !isMountedRef.current ||
          (!isActiveRef.current && !isIntroPlayingRef.current)
        ) {
          URL.revokeObjectURL(blobUrl);
          musicAudioBlobUrlRef.current = null;
          return;
        }

        musicAudioRef.current = audio;
        loadedMusicTrackIdRef.current = musicTrackId;

        const p = audio.play();
        if (p && typeof p.then === 'function') {
          p.catch(() => {});
        }
      } catch {
        // fail silently
      }
    })();
  }, [musicEnabledOn, musicTrackFromDb, musicTrackId]);

  const handleToggle = () => {
    if (isIntroPlaying) return;

    // When the user toggles the session, mark whether it was a manual pause
    // (i.e., toggling while active -> paused). This allows us to suppress
    // the 'lost focus' auto-pause modal when the user intentionally paused.
    if (isActive) {
      manuallyPausedRef.current = true;
    } else {
      manuallyPausedRef.current = false;
    }

    // If a shareable URL preset is loaded (presetSlug) then pressing Start
    // should begin a 10s intro/countdown rather than immediately starting.
    if (!isActive && !hasStarted && isUrlPreset && !introAudioRef.current) {
      // Prime intro audio during the user's click so later programmatic play() succeeds.
      (async () => {
        try {
          const introUrl = `${AUDIO_BASE}/focus_on_breath_intro.mp3`;
          const res = await fetch(introUrl);
          if (!res.ok) return;
          const blob = await res.blob();
          const blobUrl = URL.createObjectURL(blob);
          const audio = new Audio(blobUrl);
          audio.loop = false;
          audio.addEventListener('canplay', () => {
            console.debug('[Breathing] affirmation audio canplay (blob)', {
              src: audio.src,
              duration: audio.duration,
            });
          });
          audio.addEventListener('loadeddata', () => {
            console.debug('[Breathing] affirmation audio loadeddata (blob)', {
              src: audio.src,
              duration: audio.duration,
            });
          });
          audio.volume = 0.9;
          const p = audio.play();
          if (p && typeof p.then === 'function') {
            p.then(() => {
              try {
                audio.pause();
                audio.currentTime = 0;
              } catch (e) {
                // ignore
              }
              introAudioRef.current = audio;
              introAudioPrimedRef.current = true;
            }).catch(() => {
              introAudioRef.current = audio;
            });
          } else {
            introAudioRef.current = audio;
            introAudioPrimedRef.current = true;
          }
        } catch (e) {
          /* ignore */
        }
      })();
    }

    if (
      !isActive &&
      sessionUsesCustomDuration &&
      !hasCustomTimeFeature &&
      locState?.skipCustomTimeGate !== true
    ) {
      showErrorToast('This feature is locked.');
      return;
    }

    if (!isActive) {
      stopCompletionAudio();
      completionCelebratedRef.current = false;
      if (hasStarted) {
        setIsActive(true);
        isActiveRef.current = true;
        ensureBackgroundAudio();
        ensureMusicAudio();
        if (backgroundVideoRef.current) {
          const videoPlay = backgroundVideoRef.current.play();
          if (videoPlay && typeof videoPlay.then === 'function') {
            videoPlay.catch(() => {});
          }
        }
        playAffirmationAudio();
        return;
      }

      setCompletedCycles(0);
      setSessionRemaining(totalSessionSeconds);
      lastPhaseIndexRef.current = 0;
      setHasStarted(true);
      setCurrentPhaseIndex(0);
      setTimeRemaining(firstPhase.duration / 1000);
      if (backgroundVideoRef.current) {
        const videoPlay = backgroundVideoRef.current.play();
        if (videoPlay && typeof videoPlay.then === 'function') {
          videoPlay.catch(() => {});
        }
      }

      ensureBackgroundAudio();
      ensureMusicAudio();

      if (introAudioRef.current && !introAudioPrimedRef.current) {
        introAudioRef.current.pause();
        try {
          if (introAudioRef.current.src) {
            introAudioRef.current.src = '';
            introAudioRef.current.load();
          } else {
            introAudioRef.current.removeAttribute('src');
          }
        } catch (e) {
          /* ignore */
        }
        introAudioRef.current = null;
      } else if (introAudioRef.current && introAudioPrimedRef.current) {
        // Keep primed intro audio available for playback; mark as used.
        introAudioPrimedRef.current = false;
      }

      const skipForReplay = skipIntroAudioRef.current;
      skipIntroAudioRef.current = false;
      const shouldPlayIntroVoice = introVoiceOn && !skipForReplay;
      if (!shouldPlayIntroVoice) {
        setIntroTimeRemaining(0);
        setIsIntroPlaying(false);
        isIntroPlayingRef.current = false;
        setIsActive(true);
        isActiveRef.current = true;
        playAffirmationAudio();
        setAudioDebug(
          skipForReplay
            ? 'audio: intro skipped for replay'
            : 'audio: intro disabled'
        );
        return;
      }

      setIsIntroPlaying(true);
      isIntroPlayingRef.current = true;

      // Download intro audio as blob to work around streaming issues
      const introUrl = `${AUDIO_BASE}/focus_on_breath_intro.mp3`;

      const onEnded = () => {
        console.debug(
          '[Breathing] Intro audio onEnded - switching to active and playAffirmationAudio'
        );
        if (introCountdownRef.current) {
          clearInterval(introCountdownRef.current);
          introCountdownRef.current = null;
        }
        setIsIntroPlaying(false);
        isIntroPlayingRef.current = false;
        setIsActive(true);
        isActiveRef.current = true;
        playAffirmationAudio();
      };

      const onError = () => {
        console.warn(
          '[Breathing] Intro audio onError - switching to active and playAffirmationAudio'
        );
        if (introCountdownRef.current) {
          clearInterval(introCountdownRef.current);
          introCountdownRef.current = null;
        }
        setIsIntroPlaying(false);
        isIntroPlayingRef.current = false;
        setIsActive(true);
        isActiveRef.current = true;
        playAffirmationAudio();
      };

      const onPlaying = () => {
        if (introCountdownRef.current) return;
        setIntroTimeRemaining(10);
        introCountdownRef.current = setInterval(() => {
          setIntroTimeRemaining((prev) => {
            if (prev <= 1) {
              if (introCountdownRef.current) {
                clearInterval(introCountdownRef.current);
                introCountdownRef.current = null;
              }
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      };

      if (introAudioRef.current) {
        // Use already primed intro audio created on the user's click; attach
        // the event listeners and play it.
        const audio = introAudioRef.current;
        audio.addEventListener('playing', onPlaying, { once: true });
        audio.addEventListener('ended', onEnded, { once: true });
        audio.addEventListener('error', onError, { once: true });
        // Attempt to play; catch errors and fall back to fetch path if needed.
        try {
          const p = audio.play();
          if (p && typeof p.then === 'function') {
            p.catch(() => {
              // Fall back on fetch path if playing fails
              throw new Error('play() blocked');
            });
          }
        } catch (e) {
          // If playback failed, try the fetch path.
          fetch(introUrl)
            .then((res) =>
              res.ok ? res.blob() : Promise.reject(`HTTP ${res.status}`)
            )
            .then((blob) => {
              const blobUrl = URL.createObjectURL(blob);
              const audio2 = new Audio(blobUrl);
              introAudioRef.current = audio2;

              audio2.addEventListener('playing', onPlaying, { once: true });
              audio2.addEventListener('ended', onEnded, { once: true });
              audio2.addEventListener('error', onError, { once: true });

              return audio2.play();
            })
            .catch((err) => {
              console.warn('Intro audio failed:', introUrl, err);
              onError();
            });
        }
      } else {
        // Download intro audio as blob to work around streaming issues
        fetch(introUrl)
          .then((res) =>
            res.ok ? res.blob() : Promise.reject(`HTTP ${res.status}`)
          )
          .then((blob) => {
            const blobUrl = URL.createObjectURL(blob);
            const audio = new Audio(blobUrl);
            introAudioRef.current = audio;

            audio.addEventListener('playing', onPlaying, { once: true });
            audio.addEventListener('ended', onEnded, { once: true });
            audio.addEventListener('error', onError, { once: true });

            return audio.play();
          })
          .catch((err) => {
            console.warn('Intro audio failed:', introUrl, err);
            onError();
          });
      }
      return;
    }

    setIsActive(false);
    isActiveRef.current = false;
    if (phaseAudioRef.current) {
      phaseAudioRef.current.pause();
      try {
        if (phaseAudioRef.current.src) {
          phaseAudioRef.current.src = '';
          phaseAudioRef.current.load();
        } else {
          phaseAudioRef.current.removeAttribute('src');
        }
      } catch (e) {
        /* ignore */
      }
      phaseAudioRef.current = null;
    }
    if (voiceAudioRef.current) {
      voiceAudioRef.current.pause();
      try {
        if (voiceAudioRef.current.src) {
          voiceAudioRef.current.src = '';
          voiceAudioRef.current.load();
        } else {
          voiceAudioRef.current.removeAttribute('src');
        }
      } catch (e) {
        /* ignore */
      }
      voiceAudioRef.current = null;
    }
    if (backgroundAudioRef.current) {
      try {
        backgroundAudioRef.current.pause();
      } catch (e) {
        /* ignore pause errors */
      }
    }
    if (musicAudioRef.current) {
      try {
        musicAudioRef.current.pause();
      } catch (e) {
        /* ignore */
      }
    }
    if (affirmationAudioRef.current) {
      try {
        affirmationAudioRef.current.pause();
      } catch (e) {
        /* ignore pause errors */
      }
    }
    stopCompletionAudio();
  };

  handleToggleRef.current = handleToggle;

  const stopPhaseAudio = useCallback(() => {
    if (phaseAudioRef.current) {
      phaseAudioRef.current.pause();
      try {
        if (phaseAudioRef.current.src) {
          phaseAudioRef.current.src = '';
          phaseAudioRef.current.load();
        } else {
          phaseAudioRef.current.removeAttribute('src');
        }
      } catch (e) {
        /* ignore */
      }
      phaseAudioRef.current = null;
    }
  }, []);

  const stopVoiceAudio = useCallback(() => {
    if (voiceAudioRef.current) {
      voiceAudioRef.current.pause();
      try {
        if (voiceAudioRef.current.src) {
          voiceAudioRef.current.src = '';
          voiceAudioRef.current.load();
        } else {
          voiceAudioRef.current.removeAttribute('src');
        }
      } catch (e) {
        /* ignore */
      }
      voiceAudioRef.current = null;
    }
  }, []);

  const stopBackgroundAudio = useCallback(() => {
    if (backgroundAudioRef.current) {
      backgroundAudioRef.current.pause();
      try {
        if (backgroundAudioRef.current.src) {
          backgroundAudioRef.current.src = '';
          backgroundAudioRef.current.load();
        } else {
          backgroundAudioRef.current.removeAttribute('src');
        }
      } catch (e) {
        /* ignore */
      }
      backgroundAudioRef.current = null;
    }
  }, []);

  const stopMusicAudio = useCallback(() => {
    if (musicAudioRef.current) {
      musicAudioRef.current.pause();
      try {
        if (musicAudioRef.current.src) {
          musicAudioRef.current.src = '';
          musicAudioRef.current.load();
        } else {
          musicAudioRef.current.removeAttribute('src');
        }
      } catch (e) {
        /* ignore */
      }
      musicAudioRef.current = null;
    }

    if (musicAudioBlobUrlRef.current) {
      try {
        URL.revokeObjectURL(musicAudioBlobUrlRef.current);
      } catch (e) {
        /* ignore */
      }
      musicAudioBlobUrlRef.current = null;
    }

    loadedMusicTrackIdRef.current = null;
  }, []);

  const pauseBackgroundAudioForAutoPause = useCallback(() => {
    if (backgroundAudioRef.current) {
      try {
        backgroundAudioRef.current.pause();
      } catch (e) {
        /* ignore */
      }
    }
  }, []);

  const pauseMusicAudioForAutoPause = useCallback(() => {
    if (musicAudioRef.current) {
      try {
        musicAudioRef.current.pause();
      } catch (e) {
        /* ignore */
      }
    }
  }, []);

  const clearAffirmationLoopTimeout = useCallback(() => {
    if (affirmationLoopTimeoutRef.current !== null) {
      clearTimeout(affirmationLoopTimeoutRef.current);
      affirmationLoopTimeoutRef.current = null;
    }
  }, []);

  const clearAffirmationFadeInterval = useCallback(() => {
    if (affirmationFadeIntervalRef.current !== null) {
      clearInterval(affirmationFadeIntervalRef.current);
      affirmationFadeIntervalRef.current = null;
    }
  }, []);

  const detachAffirmationLoopHandler = useCallback(
    (audio?: HTMLAudioElement | null) => {
      if (affirmationLoopHandlerRef.current && audio) {
        audio.removeEventListener('ended', affirmationLoopHandlerRef.current);
      }
      affirmationLoopHandlerRef.current = null;
    },
    []
  );

  const stopAffirmationAudio = useCallback(() => {
    if (affirmationFetchAbortRef.current) {
      try {
        affirmationFetchAbortRef.current.abort();
      } catch (e) {
        /* ignore */
      }
      affirmationFetchAbortRef.current = null;
    }
    detachAffirmationLoopHandler(affirmationAudioRef.current);
    clearAffirmationLoopTimeout();
    clearAffirmationFadeInterval();
    if (affirmationAudioRef.current) {
      affirmationAudioRef.current.pause();
      try {
        if (affirmationAudioRef.current.src) {
          affirmationAudioRef.current.src = '';
          affirmationAudioRef.current.load();
        } else {
          affirmationAudioRef.current.removeAttribute('src');
        }
      } catch (e) {
        /* ignore */
      }
      affirmationAudioRef.current = null;
    }
    if (affirmationAudioUrlRef.current) {
      try {
        URL.revokeObjectURL(affirmationAudioUrlRef.current);
      } catch (e) {
        /* ignore */
      }
      affirmationAudioUrlRef.current = null;
    }
  }, [
    detachAffirmationLoopHandler,
    clearAffirmationLoopTimeout,
    clearAffirmationFadeInterval,
  ]);

  const pauseAffirmationAudioForAutoPause = useCallback(() => {
    clearAffirmationLoopTimeout();
    if (affirmationAudioRef.current) {
      const audio = affirmationAudioRef.current;
      clearAffirmationFadeInterval();
      let currentVol = audio.volume;
      affirmationFadeIntervalRef.current = setInterval(() => {
        currentVol -= 0.05;
        if (currentVol <= 0) {
          audio.volume = 0;
          audio.pause();
          clearAffirmationFadeInterval();
        } else {
          audio.volume = currentVol;
        }
      }, 50);
    }
  }, [clearAffirmationLoopTimeout, clearAffirmationFadeInterval]);

  const stopCompletionAudio = useCallback(() => {
    if (completionAudioRef.current) {
      completionAudioRef.current.pause();
      try {
        if (completionAudioRef.current.src) {
          completionAudioRef.current.src = '';
          completionAudioRef.current.load();
        } else {
          completionAudioRef.current.removeAttribute('src');
        }
      } catch (e) {
        /* ignore */
      }
      completionAudioRef.current = null;
    }
  }, []);

  const playAffirmationAudio = useCallback(() => {
    if (!affirmationsOn || !isMountedRef.current) return;

    const startPlayback = (audio: HTMLAudioElement) => {
      if (!isMountedRef.current) return;
      // When explicitly starting playback, we check if we should still be playing.
      // We allow it if either active or intro is playing.
      if (!isActiveRef.current && !isIntroPlayingRef.current) {
        // If we shouldn't be playing, ensure audio is paused.
        audio.pause();
        return;
      }

      clearAffirmationLoopTimeout();
      clearAffirmationFadeInterval();
      const targetVolume = affirmationWhisperModeOn ? 0.005 : 0.6;
      audio.volume = 0;

      console.debug('[Breathing] playAffirmationAudio starting playback', {
        src: audio.src,
        targetVolume,
        whisperMode: affirmationWhisperModeOn,
        isActive: isActiveRef.current,
        isIntro: isIntroPlayingRef.current,
      });

      const playPromise = audio.play();

      const fadeIn = () => {
        if (!isMountedRef.current || affirmationAudioRef.current !== audio) {
          console.debug(
            '[Breathing] playAffirmationAudio fadeIn aborted - not mounted or different audio'
          );
          return;
        }
        let currentVol = 0;
        affirmationFadeIntervalRef.current = setInterval(() => {
          if (!isMountedRef.current || affirmationAudioRef.current !== audio) {
            clearAffirmationFadeInterval();
            return;
          }
          currentVol += 0.02; // Slightly faster fade for better feedback
          if (currentVol >= targetVolume) {
            audio.volume = targetVolume;
            clearAffirmationFadeInterval();
            console.debug('[Breathing] playAffirmationAudio fadeIn complete', {
              finalVolume: audio.volume,
            });
          } else {
            audio.volume = currentVol;
          }
        }, 50);
      };

      if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(() => {
            console.debug(
              '[Breathing] playAffirmationAudio playPromise resolved'
            );
            if (isMountedRef.current && affirmationAudioRef.current === audio) {
              fadeIn();
            }
          })
          .catch((err) => {
            setAudioDebug('audio: affirmation play() rejected');
            console.warn(
              '[Breathing] playAffirmationAudio play() rejected',
              err
            );
          });
      } else {
        console.debug(
          '[Breathing] playAffirmationAudio play invoked (no promise support)'
        );
        fadeIn();
        setAudioDebug('audio: affirmation play invoked (no promise)');
      }
    };

    if (affirmationAudioRef.current) {
      try {
        startPlayback(affirmationAudioRef.current);
      } catch (err) {
        console.warn('Affirmation resume failed', err);
      }
      return;
    }

    if (!affirmationAudioSrc) {
      setAudioDebug('audio: affirmation source missing');
      console.warn('[Breathing] affirmation audio src missing', {
        affirmationTypeKey,
        affirmationAudioUrlOverride: locState?.affirmationAudioUrl ?? null,
      });
      return;
    }

    try {
      if (affirmationFetchAbortRef.current) {
        try {
          affirmationFetchAbortRef.current.abort();
        } catch (e) {
          /* ignore */
        }
        affirmationFetchAbortRef.current = null;
      }

      setAudioDebug(`audio: affirmation downloading ${affirmationAudioSrc}`);
      (async () => {
        const controller = new AbortController();
        affirmationFetchAbortRef.current = controller;
        try {
          const res = await fetch(affirmationAudioSrc, {
            signal: controller.signal,
          });
          if (!res.ok) {
            setAudioDebug(
              `audio: affirmation fetch ${res.status} ${affirmationAudioSrc}`
            );
            return;
          }
          const blob = await res.blob();
          if (controller.signal.aborted || !isMountedRef.current) return;

          const blobUrl = URL.createObjectURL(blob);

          // If navigation/stop occurred while downloading, don't start playback.
          if (
            controller.signal.aborted ||
            affirmationFetchAbortRef.current !== controller ||
            !affirmationsOn ||
            !isMountedRef.current
          ) {
            try {
              URL.revokeObjectURL(blobUrl);
            } catch (e) {
              /* ignore */
            }
            return;
          }

          affirmationAudioUrlRef.current = blobUrl;
          const audio = new Audio(blobUrl);
          audio.loop = false;
          // Initial volume is handled by startPlayback fadeIn
          const handleLoopEnded = () => {
            if (
              !isMountedRef.current ||
              affirmationAudioRef.current !== audio ||
              !affirmationsOn
            ) {
              return;
            }
            clearAffirmationLoopTimeout();
            affirmationLoopTimeoutRef.current = setTimeout(() => {
              if (
                isMountedRef.current &&
                affirmationAudioRef.current === audio &&
                (isActiveRef.current || isIntroPlayingRef.current)
              ) {
                console.debug('[Breathing] Affirmation loop repeating');
                startPlayback(audio);
              }
            }, AFFIRMATION_LOOP_DELAY_MS);
          };
          affirmationLoopHandlerRef.current = handleLoopEnded;
          audio.addEventListener('ended', handleLoopEnded);
          audio.addEventListener('error', (e) => {
            console.error(
              '[Breathing] Affirmation audio object error event',
              e
            );
            setAudioDebug(`audio: affirmation failed ${affirmationAudioSrc}`);
          });
          affirmationAudioRef.current = audio;
          startPlayback(audio);
        } catch (error) {
          const maybeAbortError = error as { name?: string };
          if (
            controller.signal.aborted ||
            maybeAbortError?.name === 'AbortError' ||
            !isMountedRef.current
          ) {
            return;
          }
          console.error(
            '[Breathing] playAffirmationAudio download/init failed, trying direct playback',
            error
          );

          // Fallback: try direct playback without blob proxy if fetch fails
          const directAudio = new Audio(affirmationAudioSrc);
          directAudio.loop = false;
          directAudio.addEventListener('canplay', () => {
            console.debug('[Breathing] affirmation audio canplay (direct)', {
              src: directAudio.src,
              duration: directAudio.duration,
            });
          });
          directAudio.addEventListener('loadeddata', () => {
            console.debug('[Breathing] affirmation audio loadeddata (direct)', {
              src: directAudio.src,
              duration: directAudio.duration,
            });
          });
          directAudio.addEventListener('error', (e) => {
            console.error(
              '[Breathing] Affirmation direct audio error event',
              e
            );
            setAudioDebug(
              `audio: affirmation direct failed ${affirmationAudioSrc}`
            );
          });
          affirmationAudioRef.current = directAudio;
          setAudioDebug('audio: affirmation direct fallback');
          startPlayback(directAudio);
        } finally {
          if (affirmationFetchAbortRef.current === controller) {
            affirmationFetchAbortRef.current = null;
          }
        }
      })();
    } catch (error) {
      setAudioDebug('audio: affirmation unexpected error');
      console.error(error);
    }
  }, [
    affirmationsOn,
    affirmationWhisperModeOn,
    affirmationAudioSrc,
    clearAffirmationLoopTimeout,
    clearAffirmationFadeInterval,
    setAudioDebug,
  ]);

  useEffect(() => {
    if (!affirmationsOn) {
      stopAffirmationAudio();
    }
    return () => {
      stopAffirmationAudio();
    };
  }, [affirmationsOn, stopAffirmationAudio]);

  const playCompletionAudio = useCallback(() => {
    stopCompletionAudio();
    if (!completionSoundPaths.length) return;
    const index = Math.floor(Math.random() * completionSoundPaths.length);
    const selectedPath = completionSoundPaths[index]!;

    // Download as blob to work around streaming issues
    fetch(selectedPath)
      .then((res) =>
        res.ok ? res.blob() : Promise.reject(`HTTP ${res.status}`)
      )
      .then((blob) => {
        if (!isMountedRef.current) return;
        const blobUrl = URL.createObjectURL(blob);
        const audio = new Audio(blobUrl);
        audio.volume = 0.45;
        audio.addEventListener(
          'ended',
          () => {
            stopCompletionAudio();
            URL.revokeObjectURL(blobUrl);
          },
          { once: true }
        );
        audio.addEventListener('error', () => {
          setAudioDebug(`audio: completion failed ${selectedPath}`);
        });

        if (!isMountedRef.current) {
          URL.revokeObjectURL(blobUrl);
          return;
        }

        completionAudioRef.current = audio;
        return audio.play();
      })
      .then(() => {
        setAudioDebug(`audio: completion -> ${selectedPath}`);
      })
      .catch((err) => {
        console.warn('Completion audio failed:', selectedPath, err);
        setAudioDebug(`audio: completion failed ${selectedPath}`);
      });
  }, [stopCompletionAudio]);

  const launchHeartConfetti = useCallback(() => {
    const defaults: Options = {
      spread: 70,
      ticks: 200,
      gravity: 0.5,
      scalar: 1.1,
      colors: ['#fb7185', '#f472b6', '#fca5a5', '#f87171', '#ffffff'],
      shapes: ['heart'] as unknown as Options['shapes'],
    };

    // Big three bursts (left, center, right)
    const mainBursts = [
      { particleCount: 140, origin: { x: 0.18, y: 0.6 }, startVelocity: 25 },
      { particleCount: 200, origin: { x: 0.5, y: 0.45 }, startVelocity: 30 },
      { particleCount: 140, origin: { x: 0.82, y: 0.6 }, startVelocity: 25 },
    ];

    mainBursts.forEach((b, i) => {
      setTimeout(() => {
        confetti({ ...defaults, ...b });
      }, i * 200);
    });

    // Follow-up showers to create a fuller effect
    for (let i = 0; i < 24; i++) {
      const delay = 600 + i * 40;
      const count = 20 + Math.floor(Math.random() * 60);
      const ox = 0.1 + Math.random() * 0.8;
      const oy = 0.3 + Math.random() * 0.5;
      const sv = 10 + Math.floor(Math.random() * 20);
      setTimeout(() => {
        confetti({
          ...defaults,
          particleCount: count,
          origin: { x: ox, y: oy },
          startVelocity: sv,
          spread: 50 + Math.floor(Math.random() * 40),
        });
      }, delay);
    }
  }, []);

  const handleReset = useCallback(() => {
    sessionStartedRef.current = false;
    sessionCompletedRef.current = false;
    setIsActive(false);
    isActiveRef.current = false;
    manuallyPausedRef.current = false;
    setIsIntroPlaying(false);
    isIntroPlayingRef.current = false;
    setHasStarted(false);
    setCurrentPhaseIndex(0);
    setTimeRemaining(firstPhase.duration / 1000);
    setIntroTimeRemaining(10);
    setCompletedCycles(0);
    setSessionRemaining(totalSessionSeconds);
    lastPhaseIndexRef.current = 0;
    completionCelebratedRef.current = false;
    setShowCompleteModal(false);
    setPublicSaveTimeRemaining(30);
    setIsPublicSaveExpired(false);

    if (introAudioRef.current) {
      introAudioRef.current.pause();
      try {
        if (introAudioRef.current.src) {
          introAudioRef.current.src = '';
          introAudioRef.current.load();
        } else {
          introAudioRef.current.removeAttribute('src');
        }
      } catch (e) {
        /* ignore */
      }
      introAudioRef.current = null;
    }

    if (introCountdownRef.current) {
      clearInterval(introCountdownRef.current);
      introCountdownRef.current = null;
    }

    if (publicSaveTimerRef.current) {
      clearInterval(publicSaveTimerRef.current);
      publicSaveTimerRef.current = null;
    }

    stopPhaseAudio();
    stopVoiceAudio();
    stopBackgroundAudio();
    stopMusicAudio();
    stopAffirmationAudio();
    stopCompletionAudio();
    phaseTickRef.current = null;
  }, [
    firstPhase.duration,
    totalSessionSeconds,
    stopPhaseAudio,
    stopVoiceAudio,
    stopBackgroundAudio,
    stopMusicAudio,
    stopAffirmationAudio,
    stopCompletionAudio,
  ]);

  const navigateToDashboardAfterSession = useCallback(() => {
    try {
      handleReset();
    } catch (e) {
      /* ignore */
    }
    navigate('/dashboard');
  }, [handleReset, navigate]);

  const handleUserPauseToggle = useCallback(() => {
    // If session is active, pause and show pause modal.
    if (isActive) {
      logBreathingInteraction({
        eventType: 'control_click',
        eventCategory: 'pause',
      });
      try {
        handleToggleRef.current();
      } catch (e) {
        /* ignore */
      }
      // Mark the pause as being user-initiated so we don't confuse it
      // with an auto-pause caused by tab visibility changes.
      manuallyPausedRef.current = true;
      setShowPauseModal(true);
      return;
    }
    if (hasStarted) {
      logBreathingInteraction({
        eventType: 'control_click',
        eventCategory: 'resume',
      });
    }
    // If paused, behave like a normal toggle (resume)
    try {
      handleToggleRef.current();
    } catch (e) {
      /* ignore */
    }
    setShowPauseModal(false);
  }, [hasStarted, isActive, logBreathingInteraction]);

  const handleRestart = useCallback(() => {
    logBreathingInteraction({
      eventType: 'control_click',
      eventCategory: 'reset',
    });
    // Reset then immediately start a fresh session
    try {
      handleReset();
    } catch (e) {
      /* ignore */
    }
    // Clear manual pause marker
    manuallyPausedRef.current = false;
    setShowPauseModal(false);
    // Start the session in next tick so reset fully applied
    setTimeout(() => {
      try {
        handleToggleRef.current();
      } catch (e) {
        /* ignore */
      }
    }, 0);
  }, [handleReset, logBreathingInteraction]);

  const handleContinue = useCallback(() => {
    logBreathingInteraction({
      eventType: 'control_click',
      eventCategory: 'resume',
    });
    try {
      handleToggleRef.current();
    } catch (e) {
      /* ignore */
    }
    setShowPauseModal(false);
  }, [logBreathingInteraction]);

  const handleQuit = useCallback(() => {
    if (hasStarted && sessionRemaining > 0) {
      logBreathingInteraction({
        eventType: 'session_stop',
        eventCategory: 'lifecycle',
        eventValue: { remaining_seconds: sessionRemaining },
      });
    }
    try {
      handleReset();
    } catch (e) {
      /* ignore */
    }
    setShowPauseModal(false);
    navigate('/dashboard');
  }, [
    handleReset,
    hasStarted,
    logBreathingInteraction,
    navigate,
    sessionRemaining,
  ]);

  useEffect(() => {
    if (!isActive) return;
    if (sessionRemaining > 0) return;

    if (!sessionCompletedRef.current) {
      sessionCompletedRef.current = true;
      logBreathingInteraction({
        eventType: 'session_complete',
        eventCategory: 'lifecycle',
      });
    }

    if (timerRef.current !== null) {
      cancelAnimationFrame(timerRef.current);
      timerRef.current = null;
    }
    if (phaseTimerRef.current) {
      clearTimeout(phaseTimerRef.current);
      phaseTimerRef.current = null;
    }
    if (sessionIntervalRef.current) {
      clearInterval(sessionIntervalRef.current);
      sessionIntervalRef.current = null;
    }

    stopPhaseAudio();
    stopVoiceAudio();
    stopBackgroundAudio();
    stopMusicAudio();
    stopAffirmationAudio();

    if (!completionCelebratedRef.current) {
      launchHeartConfetti();
      completionCelebratedRef.current = true;
    }

    playCompletionAudio();

    if (introCountdownRef.current) {
      clearInterval(introCountdownRef.current);
      introCountdownRef.current = null;
    }

    setCompletedCycles(totalCycles);
    setCurrentPhaseIndex(0);
    lastPhaseIndexRef.current = 0;

    setIsActive(false);
    isActiveRef.current = false;
    setIsIntroPlaying(false);
    isIntroPlayingRef.current = false;
    setHasStarted(false);
    setIntroTimeRemaining(10);
    setTimeRemaining(0);

    if (backgroundVideoRef.current) {
      backgroundVideoRef.current.pause();
      try {
        backgroundVideoRef.current.currentTime = 0;
      } catch (e) {
        /* ignore seek errors */
      }
    }

    // Show completion modal for both guests and signed-in users
    setShowCompleteModal(true);
  }, [
    isActive,
    launchHeartConfetti,
    playCompletionAudio,
    sessionRemaining,
    stopBackgroundAudio,
    stopMusicAudio,
    stopAffirmationAudio,
    stopPhaseAudio,
    stopVoiceAudio,
    totalCycles,
    logBreathingInteraction,
  ]);

  // Public save countdown timer (only relevant for signed-in users)
  useEffect(() => {
    if (publicSaveTimerRef.current) {
      clearInterval(publicSaveTimerRef.current);
      publicSaveTimerRef.current = null;
    }

    if (!showCompleteModal) {
      setPublicSaveTimeRemaining(30);
      setIsPublicSaveExpired(false);
      return;
    }

    setPublicSaveTimeRemaining(30);
    setIsPublicSaveExpired(false);

    if (!isAuthenticated) {
      return;
    }

    publicSaveTimerRef.current = setInterval(() => {
      setPublicSaveTimeRemaining((prev) => {
        if (prev <= 1) {
          setIsPublicSaveExpired(true);
          if (publicSaveTimerRef.current) {
            clearInterval(publicSaveTimerRef.current);
            publicSaveTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (publicSaveTimerRef.current) {
        clearInterval(publicSaveTimerRef.current);
        publicSaveTimerRef.current = null;
      }
    };
  }, [showCompleteModal, isAuthenticated]);

  useEffect(() => {
    if (!showCompleteModal && pendingReplayRef.current) {
      pendingReplayRef.current = false;
      setTimeout(() => {
        handleToggleRef.current();
      }, 3000);
    }
  }, [showCompleteModal]);

  // Check for newly unlocked badges
  const checkForNewBadges = useCallback(async () => {
    if (!user?.id) return;

    try {
      const newBadges = await fetchUnnotifiedBadges(user.id);
      if (newBadges.length > 0) {
        setUnlockedBadges(newBadges);
        setShowBadgeModal(true);

        // Mark badges as notified
        const badgeIds = newBadges.map((b) => b.id);
        await markBadgesNotified(badgeIds);
        await refreshUserFeatures();
      }
    } catch (error) {
      console.error('Failed to check for new badges:', error);
      // Don't show error to user - badge notification is non-critical
    }
  }, [user?.id, refreshUserFeatures]);

  const handleModalClose = useCallback(() => {
    setShowCompleteModal(false);
    stopAffirmationAudio();
    if (publicSaveTimerRef.current) {
      clearInterval(publicSaveTimerRef.current);
      publicSaveTimerRef.current = null;
    }
  }, [stopAffirmationAudio]);

  const handleReplaySession = useCallback(() => {
    pendingReplayRef.current = true;
    skipIntroAudioRef.current = true;
    handleModalClose();
    handleReset();
  }, [handleModalClose, handleReset]);

  // Refs and state for automatic pause when the tab/window loses focus
  const wasActiveBeforeHiddenRef = useRef(false);
  const wasIntroPlayingBeforeHiddenRef = useRef(false);
  const autoPausedRef = useRef(false);
  // Track whether the user manually paused the session. When true we
  // suppress the lost-focus auto-pause modal because the session is
  // already paused by the user and showing the toast would be redundant.
  const manuallyPausedRef = useRef(false);
  const autoPauseToastIdRef = useRef<string | null>(null);

  const showPersistentSessionToast = useCallback(
    (
      message: string,
      onContinue: () => void,
      onGoToDashboard: () => void,
      primaryLabel?: string,
      onRestart?: () => void
    ) =>
      toastLib.custom(
        (t: Toast) => (
          <PersistentSessionToast
            message={message}
            toastId={t.id}
            onContinue={onContinue}
            onGoToDashboard={onGoToDashboard}
            primaryLabel={primaryLabel}
            onRestart={onRestart}
          />
        ),
        {
          duration: Infinity,
          position: 'top-center',
        }
      ),
    []
  );

  const showAutoPausedToast = useCallback(() => {
    if (autoPauseToastIdRef.current) return;

    const id = showPersistentSessionToast(
      'Session paused — this tab lost focus or was minimized.',
      () => {
        // Resume: clear auto-paused flag and attempt to resume using the existing toggle
        autoPausedRef.current = false;
        autoPauseToastIdRef.current = null;
        logBreathingInteraction({
          eventType: 'control_click',
          eventCategory: 'resume',
        });
        // Resume only when the session was active before we auto-paused.
        if (wasActiveBeforeHiddenRef.current) {
          try {
            handleToggleRef.current();
          } catch (e) {
            /* ignore */
          }
        } else if (wasIntroPlayingBeforeHiddenRef.current) {
          // Fallback: if the intro was playing, attempt to re-enter the intro path
          try {
            // If the session hasn't fully started calling toggle will re-run the
            // same initialization path that starts the intro; otherwise we
            // simply set the intro flag and allow the same effects to re-run.
            if (!hasStarted) {
              handleToggleRef.current();
            } else {
              setIsIntroPlaying(true);
            }
          } catch (e) {
            /* ignore */
          }
        }
      },
      () => {
        // Stop session and go to dashboard
        autoPausedRef.current = false;
        autoPauseToastIdRef.current = null;
        if (hasStarted && sessionRemainingRef.current > 0) {
          logBreathingInteraction({
            eventType: 'session_stop',
            eventCategory: 'lifecycle',
            eventValue: {
              remaining_seconds: sessionRemainingRef.current,
              reason: 'auto_pause_exit',
            },
          });
        }
        navigateToDashboardAfterSession();
      },
      'Resume Session',
      handleRestart
    );

    // toastLib.custom tends to return an id string/number — store as string
    autoPauseToastIdRef.current = id ? String(id) : null;
  }, [
    navigateToDashboardAfterSession,
    showPersistentSessionToast,
    hasStarted,
    handleRestart,
    logBreathingInteraction,
  ]);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined')
      return;

    const pauseIfHidden = () => {
      // If Play in Background is enabled, do not auto-pause when hidden.
      if (locState?.playInBackground) return;

      // If the user manually paused the session, do not auto-pause / show
      // the persistent session toast when the tab loses focus. This avoids
      // redundant modal showing when the session is intentionally paused.
      if (manuallyPausedRef.current) return;
      const hidden = Boolean(
        document.hidden || document.visibilityState === 'hidden'
      );
      if (!hidden) return;

      if (isActive || isIntroPlaying || hasStarted) {
        logBreathingInteraction({
          eventType: 'interruption',
          eventCategory: 'visibility',
        });
        wasActiveBeforeHiddenRef.current = isActive;
        wasIntroPlayingBeforeHiddenRef.current = isIntroPlaying;
        autoPausedRef.current = true;

        // Pause state and stop media (existing helpers will clean timers)
        try {
          setIsActive(false);
          setIsIntroPlaying(false);
        } catch (e) {
          /* ignore */
        }

        stopPhaseAudio();
        stopVoiceAudio();
        pauseBackgroundAudioForAutoPause();
        pauseMusicAudioForAutoPause();
        pauseAffirmationAudioForAutoPause();
        stopCompletionAudio();

        if (backgroundVideoRef.current) {
          try {
            backgroundVideoRef.current.pause();
          } catch {
            /* ignore */
          }
        }

        showAutoPausedToast();
      }
    };

    const onVisibilityChange = () => {
      if (document.hidden || document.visibilityState === 'hidden') {
        pauseIfHidden();
      } else {
        // When coming back to visible we prefer not to auto-resume (requires user action)
        if (autoPausedRef.current) {
          showAutoPausedToast();
        }
      }
    };

    const onWindowBlur = () => pauseIfHidden();
    const onWindowFocus = () => {
      if (autoPausedRef.current) showAutoPausedToast();
    };
    const onPageHide = () => pauseIfHidden();

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('focus', onWindowFocus);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('focus', onWindowFocus);
      window.removeEventListener('pagehide', onPageHide);
      if (autoPauseToastIdRef.current) {
        toastLib.dismiss(autoPauseToastIdRef.current);
        autoPauseToastIdRef.current = null;
      }
    };
  }, [
    isActive,
    isIntroPlaying,
    hasStarted,
    stopPhaseAudio,
    stopVoiceAudio,
    pauseBackgroundAudioForAutoPause,
    pauseMusicAudioForAutoPause,
    pauseAffirmationAudioForAutoPause,
    stopCompletionAudio,
    logBreathingInteraction,
    showAutoPausedToast,
    locState,
  ]);

  useEffect(() => {
    return () => {
      if (
        hasStartedRef.current &&
        (isActiveRef.current || isIntroPlayingRef.current)
      ) {
        logBreathingInteraction({
          eventType: 'interruption',
          eventCategory: 'navigation',
          eventValue: { remaining_seconds: sessionRemainingRef.current },
        });
      }
    };
  }, [logBreathingInteraction]);

  // Fade the Pause control after a few seconds of inactivity when the
  // session is active; bring it back on pointer/mouse/touch interaction.
  useEffect(() => {
    if (!hasStarted || !isActive) {
      setControlsFaded(false);
      return;
    }

    let fadeTimer: ReturnType<typeof setTimeout> | null = null;

    const scheduleFade = () => {
      if (fadeTimer) clearTimeout(fadeTimer);
      fadeTimer = setTimeout(() => setControlsFaded(true), 4000);
    };

    const onInteraction = () => {
      setControlsFaded(false);
      scheduleFade();
    };

    // Start the initial fade schedule and attach listeners
    scheduleFade();
    document.addEventListener('pointermove', onInteraction);
    document.addEventListener('pointerdown', onInteraction);
    document.addEventListener('touchstart', onInteraction);
    document.addEventListener('mousemove', onInteraction);

    return () => {
      if (fadeTimer) clearTimeout(fadeTimer);
      document.removeEventListener('pointermove', onInteraction);
      document.removeEventListener('pointerdown', onInteraction);
      document.removeEventListener('touchstart', onInteraction);
      document.removeEventListener('mousemove', onInteraction);
    };
  }, [hasStarted, isActive]);

  const handleSignUpRedirect = useCallback(() => {
    handleModalClose();
    navigate('/signup', { state: { from: location.pathname } });
  }, [handleModalClose, navigate, location.pathname]);

  const handleSignInRedirect = useCallback(() => {
    handleModalClose();
    navigate('/signin', { state: { from: location.pathname } });
  }, [handleModalClose, navigate, location.pathname]);

  // Save handlers
  const handleSavePublic = useCallback(async () => {
    if (isPublicSaveExpired) {
      showErrorToast(
        'Time expired — this session can only be saved privately.'
      );
      return;
    }

    if (!user) {
      showErrorToast('You must be signed in to save sessions.');
      return;
    }

    try {
      // Determine matching background video id (if any) and breathing ratio id
      const backgroundVideoId = matchedBackgroundVideoId;

      await saveSession({
        user_id: user.id,
        total_duration: totalSessionSeconds,
        cycles_completed: completedCycles,
        breathing_ratio_id: matchedBreathingRatioId,
        background_videos_id: backgroundVideoId,
        voice_guidance: voiceGuidanceOn,
        phase_chime: phaseChimeOn,
        background_sound: backgroundSoundOn,
        play_in_background: locState?.playInBackground ?? false,
        intro_voice: introVoiceOn,
        music_enabled: musicEnabledOn,
        music_track_id: musicTrackId,
        hide_cycle_count: hideCycleCount,
        hide_time_left: hideTimeLeft,
        leaderboard_eligibility: true,
        // Affirmation fields
        affirmation: affirmationsOn,
        affirmation_type: affirmationsOn ? affirmationTypeKey : null,
        affirmation_mode: affirmationsOn
          ? (locState?.affirmationMode ?? null)
          : null,
        affirmation_whisper_mode: affirmationsOn
          ? affirmationWhisperModeOn
          : false,
        notes: `Ratio: ${ratioSummaryString}`,
        breathing_style: breathingStyle,
        breathing_style_id: matchedBreathingStyleId,
      });

      // Debug: fetch the user's streak row immediately after saving to validate trigger behavior
      if (user?.id) {
        try {
          const userStreak = await fetchUserStreak(user.id);
          // eslint-disable-next-line no-console
          console.debug(
            'handleSavePublic: user_streaks after save',
            userStreak
          );
        } catch (err) {
          // eslint-disable-next-line no-console
          console.debug('handleSavePublic: failed to fetch user_streaks', err);
        }
      }

      // Check for newly unlocked badges
      await checkForNewBadges();

      // Close the completion modal and show a persistent toast with actions.
      handleModalClose();

      // Keep prompting until the user explicitly chooses to continue or go to Dashboard.
      // Use default import's attached helper. Cast to a specific function type
      // to satisfy lint/type rules, and fall back to a regular toast if unavailable.
      type SessionToastFn = (
        onContinue: () => void,
        onGoToDashboard: () => void,
        message?: string
      ) => unknown;

      const sessionToast = (
        toastLib as unknown as { showSessionSavedToast?: SessionToastFn }
      ).showSessionSavedToast;

      if (sessionToast) {
        sessionToast(
          () => {
            handleReplaySession();
          },
          () => {
            navigateToDashboardAfterSession();
          },
          'Session saved and added to leaderboard!'
        );
      } else {
        showPersistentSessionToast(
          'Session saved and added to leaderboard!',
          () => {
            handleReplaySession();
          },
          () => {
            navigateToDashboardAfterSession();
          }
        );
      }
    } catch (error) {
      console.error('Failed to save session:', error);
      showErrorToast('Failed to save session. Please try again.');
    }
  }, [
    isPublicSaveExpired,
    user,
    matchedBackgroundVideoId,
    matchedBreathingRatioId,
    totalSessionSeconds,
    completedCycles,
    voiceGuidanceOn,
    introVoiceOn,
    phaseChimeOn,
    backgroundSoundOn,
    ratioSummaryString,
    handleModalClose,
    showPersistentSessionToast,
    handleReplaySession,
    checkForNewBadges,
    breathingStyle,
    matchedBreathingStyleId,
    affirmationsOn,
    affirmationTypeKey,
    affirmationWhisperModeOn,
    locState?.affirmationMode,
    locState?.playInBackground,
    musicEnabledOn,
    musicTrackId,
    navigateToDashboardAfterSession,
  ]);

  const handleSavePrivate = useCallback(async () => {
    if (!user) {
      showErrorToast('You must be signed in to save sessions.');
      return;
    }

    try {
      const backgroundVideoId = matchedBackgroundVideoId;

      await saveSession({
        user_id: user.id,
        total_duration: totalSessionSeconds,
        cycles_completed: completedCycles,
        breathing_ratio_id: matchedBreathingRatioId,
        background_videos_id: backgroundVideoId,
        voice_guidance: voiceGuidanceOn,
        phase_chime: phaseChimeOn,
        background_sound: backgroundSoundOn,
        play_in_background: locState?.playInBackground ?? false,
        intro_voice: introVoiceOn,
        music_enabled: musicEnabledOn,
        music_track_id: musicTrackId,
        hide_cycle_count: hideCycleCount,
        hide_time_left: hideTimeLeft,
        leaderboard_eligibility: false,
        // Affirmation fields
        affirmation: affirmationsOn,
        affirmation_type: affirmationsOn ? affirmationTypeKey : null,
        affirmation_mode: affirmationsOn
          ? (locState?.affirmationMode ?? null)
          : null,
        affirmation_whisper_mode: affirmationsOn
          ? affirmationWhisperModeOn
          : false,
        notes: `Ratio: ${ratioSummaryString}`,
        breathing_style: breathingStyle,
        breathing_style_id: matchedBreathingStyleId,
      });

      // Debug: fetch the user's streak row immediately after saving to validate trigger behavior
      if (user?.id) {
        try {
          const userStreak = await fetchUserStreak(user.id);
          // eslint-disable-next-line no-console
          console.debug(
            'handleSavePrivate: user_streaks after save',
            userStreak
          );
        } catch (err) {
          // eslint-disable-next-line no-console
          console.debug('handleSavePrivate: failed to fetch user_streaks', err);
        }
      }

      handleModalClose();

      showPersistentSessionToast(
        'Session saved privately.',
        () => {
          handleReplaySession();
        },
        () => {
          navigateToDashboardAfterSession();
        }
      );
    } catch (error) {
      console.error('Failed to save session:', error);
      showErrorToast('Failed to save session. Please try again.');
    }
  }, [
    user,
    matchedBackgroundVideoId,
    matchedBreathingRatioId,
    totalSessionSeconds,
    completedCycles,
    voiceGuidanceOn,
    introVoiceOn,
    phaseChimeOn,
    backgroundSoundOn,
    ratioSummaryString,
    handleModalClose,
    showPersistentSessionToast,
    handleReplaySession,
    breathingStyle,
    matchedBreathingStyleId,
    affirmationsOn,
    affirmationTypeKey,
    affirmationWhisperModeOn,
    locState?.affirmationMode,
    locState?.playInBackground,
    musicEnabledOn,
    musicTrackId,
    navigateToDashboardAfterSession,
  ]);

  useEffect(() => {
    if (!selectedVideoSrc) {
      setVideoDebug(`video: no source for key=${selectedVideoKey}`);
      return;
    }

    const v = videoEl;
    if (!v) {
      setVideoDebug('video: waiting for ref');
      return;
    }

    const eventNames: Array<keyof HTMLMediaElementEventMap> = [
      'loadstart',
      'loadeddata',
      'canplay',
      'play',
      'pause',
      'stalled',
      'suspend',
      'waiting',
      'error',
    ];

    const handleEvent = (event: Event) => {
      const currentTime = Number.isFinite(v.currentTime)
        ? v.currentTime.toFixed(2)
        : '0.00';
      const baseMessage = `video ${event.type} t=${currentTime} ready=${v.readyState} network=${v.networkState}`;
      setVideoDebug(baseMessage);
      console.debug('Breathing background video', baseMessage, {
        src: v.currentSrc || v.src,
      });
      if (event.type === 'error') {
        const mediaError = v.error;
        console.error('Breathing background video error', mediaError);
      }
    };

    eventNames.forEach((eventName) => {
      v.addEventListener(eventName, handleEvent);
    });

    v.muted = true;
    const playPromise = v.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.catch(() => {
        setVideoDebug('video play() rejected (likely autoplay policy)');
        console.warn('Breathing background video play() rejected');
      });
    } else {
      setVideoDebug('video play() invoked without promise');
    }
    return () => {
      eventNames.forEach((eventName) => {
        v.removeEventListener(eventName, handleEvent);
      });
    };
  }, [selectedVideoKey, selectedVideoSrc, videoEl]);

  return (
    <div
      className={`relative min-h-screen overflow-hidden ${
        hasStaticBackground
          ? ''
          : 'bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100'
      }`}
      style={
        isColorBackground
          ? { backgroundColor: backgroundColorValue ?? undefined }
          : isGradientBackground
            ? { backgroundImage: backgroundGradientValue ?? undefined }
            : undefined
      }
    >
      {!hasStaticBackground && videoLoading && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-gradient-to-br from-blue-200 via-teal-100 to-cyan-200">
          <div className="text-center text-gray-600">
            <div className="mb-2 text-lg" aria-hidden />
            {/* video loading progress pill removed per request */}
          </div>
        </div>
      )}

      {!hasStaticBackground &&
        (videoBlobUrl ? (
          <video
            key={`video-${selectedVideoKey}`}
            ref={(el) => {
              setVideoEl(el);
              backgroundVideoRef.current = el;
            }}
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            src={videoBlobUrl}
            autoPlay
            muted
            loop
            playsInline
            aria-hidden="true"
            onError={(e) => {
              const target = e.target as HTMLVideoElement;
              const err = target.error;
              console.error('Video element error:', {
                error: err,
                src: target.src,
                networkState: target.networkState,
                readyState: target.readyState,
              });
            }}
          />
        ) : (
          !videoLoading && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-200 via-teal-100 to-cyan-200 text-gray-500"></div>
          )
        ))}
      {isImageBackground && backgroundImageValue && (
        <div
          className="pointer-events-none absolute inset-0 z-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${backgroundImageValue}")` }}
          aria-hidden="true"
        />
      )}
      <div style={{ animationDuration: '8s' }} />

      <div className="relative z-20 flex min-h-screen flex-col items-center sm:px-4 sm:py-6">
        {/* Top spacer / header section */}
        <div className="flex w-full flex-1 flex-col items-center justify-center py-2 sm:justify-start sm:py-4">
          {presetMessage && (
            <div className="mb-2 text-center sm:mb-4">
              <div className="inline-block rounded-xl bg-white/50 px-5 py-2 text-sm text-gray-700 shadow-sm backdrop-blur-sm">
                {presetMessage}
              </div>
            </div>
          )}

          {locState?.presetName && !isUrlPreset && (
            <div className="mb-2 text-center sm:mb-4">
              <div className="inline-block rounded-xl bg-white/60 px-6 py-2 shadow-sm backdrop-blur-sm">
                <p className="text-lg font-semibold text-gray-800 sm:text-xl">
                  {locState.presetName}
                </p>
              </div>
            </div>
          )}

          {(!hideCycleCount || !hideTimeLeft) && (
            <div className="mb-4 text-center sm:mb-8">
              <div className="inline-block rounded-xl bg-white/60 px-6 py-1 shadow-sm backdrop-blur-sm">
                <p className="text-2xl font-semibold text-gray-800 sm:text-4xl">
                  {!hideCycleCount && (
                    <>
                      <span className="tabular-nums">{completedCycles}</span> of{' '}
                      <span className="tabular-nums">{totalCycles}</span> cycles
                    </>
                  )}
                  {!hideCycleCount && !hideTimeLeft && ' — '}
                  {!hideTimeLeft && (
                    <>
                      <span className="tabular-nums">
                        {formatTime(sessionRemaining)}
                      </span>{' '}
                      left
                    </>
                  )}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Middle Section (Breathing Guide) */}
        <div className="flex w-full flex-shrink-0 flex-col items-center justify-center">
          {isLineStyle ? (
            <LineBreathing
              isActive={isActive}
              currentPhase={currentPhase}
              phaseIndex={currentPhaseIndex}
              totalPhases={phaseCount}
              phaseLabel={phaseConfig.label}
              timeRemaining={timeRemaining}
              introTimeRemaining={introTimeRemaining}
              phaseProgressRatio={phaseProgressRatio}
              gradientClass={phaseConfig.color}
              linePhases={linePhases}
              introVoiceOn={introVoiceOn}
            />
          ) : (
            <div
              ref={circleContainerRef}
              className="relative flex h-64 w-64 items-center justify-center sm:h-96 sm:w-96"
            >
              <div
                className={`absolute z-0 h-full w-full rounded-full ${phaseGradientClass} blur-3xl transition-all`}
                style={{
                  transform: isActive
                    ? `scale(${phaseConfig.scale})`
                    : 'scale(1)',
                  transitionDuration: `${phaseConfig.duration}ms`,
                }}
              />

              <div
                className={`absolute z-10 flex h-48 w-48 items-center justify-center rounded-full bg-gradient-to-br sm:h-64 sm:w-64 ${phaseGradientClass} transition-all ease-in-out`}
                style={{
                  opacity: 0.8,
                  transform: isActive
                    ? `scale(${phaseConfig.scale})`
                    : 'scale(1)',
                  transitionDuration: `${phaseConfig.duration}ms`,
                }}
              >
                <div className="relative z-20 flex h-36 w-36 flex-col items-center justify-center rounded-full bg-white/90 backdrop-blur-sm sm:h-48 sm:w-48">
                  {/* top highlight to suggest curvature */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute h-36 w-36 rounded-full sm:h-48 sm:w-48"
                    style={{
                      background:
                        'radial-gradient(ellipse at 35% 20%, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.2) 30%, rgba(255,255,255,0.0) 60%)',
                      mixBlendMode: 'screen',
                      transform: 'translateY(-6px) scale(1.02)',
                      opacity: 0.9,
                    }}
                  />
                  <div className="relative z-30 text-center">
                    <p className="mb-2 max-w-[9.5rem] whitespace-normal break-words text-xl font-bold leading-tight tracking-tight text-gray-800 sm:max-w-[11.5rem] sm:text-2xl">
                      {isActive
                        ? phaseConfig.label
                        : introVoiceOn
                          ? "Let's Begin in"
                          : "Let's Begin"}
                    </p>
                    {(isActive || showReadyCountdown) && (
                      <p className="text-4xl font-semibold tabular-nums text-gray-700 sm:text-5xl">
                        {displaySeconds}
                        <span className="text-xl font-medium sm:text-2xl">
                          s
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {isActive && (
                <div
                  ref={progressRingWrapperRef}
                  className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center overflow-hidden rounded-full"
                  style={
                    ringDebug
                      ? {
                          outline: '1px dashed rgba(148, 163, 184, 0.8)',
                          outlineOffset: 4,
                        }
                      : undefined
                  }
                >
                  <svg
                    ref={svgProgressRingRef}
                    className="h-56 w-56 rounded-full sm:h-72 sm:w-72"
                    viewBox="0 0 100 100"
                    style={
                      ringDebug
                        ? { backgroundColor: 'rgba(59,130,246,0.08)' }
                        : undefined
                    }
                  >
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="text-white/30"
                    />
                    {/* White outline for contrast */}
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="#ffffff"
                      strokeWidth="14"
                      strokeLinecap="round"
                      vectorEffect="non-scaling-stroke"
                    />
                    <defs>
                      <linearGradient
                        id="progressGrad"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="0%"
                      >
                        <stop offset="0%" stopColor="var(--tw-gradient-from)" />
                        <stop offset="100%" stopColor="var(--tw-gradient-to)" />
                      </linearGradient>
                    </defs>

                    {/* Purple progress ring (now gradient) */}
                    {progressArcPath && (
                      <path
                        d={progressArcPath}
                        fill="none"
                        stroke="url(#progressGrad)"
                        strokeWidth="10"
                        strokeLinecap="round"
                        ref={progressRingCircleRef}
                        className="drop-shadow-[0_6px_18px_rgba(98,108,206,0.4)]"
                      />
                    )}

                    {/* Visible endpoint indicator to help desktop users see progress */}
                    {typeof displayPhaseProgressRatio === 'number' &&
                      (() => {
                        const t = Math.min(
                          1,
                          Math.max(0, displayPhaseProgressRatio)
                        );
                        // During hold phases, the heart indicator must remain completely still.
                        // We freeze its last computed angle from the preceding inhale/exhale and
                        // reuse it for the entire duration of the hold.
                        let angle: number;
                        if (isHoldPhase) {
                          angle = frozenRingHeartAngleRef.current;
                        } else {
                          angle =
                            currentPhase === 'exhale'
                              ? -t * Math.PI * 2 - Math.PI / 2
                              : t * Math.PI * 2 - Math.PI / 2; // rotate so 0 starts at top; reverse for exhale
                          frozenRingHeartAngleRef.current = angle;
                        }
                        const cx = 50 + Math.cos(angle) * 45;
                        const cy = 50 + Math.sin(angle) * 45;
                        return (
                          <g
                            transform={`translate(${cx}, ${cy}) scale(0.6)`}
                            aria-hidden="true"
                          >
                            {/* Small heart centered on the endpoint (approx. same visual weight as r=3.6 circle) */}
                            <path
                              d="M0,-3.6 C-2.2,-6.2 -6,-5 -6,-1.6 C-6,1.0 -3.6,3.4 0,6 C3.6,3.4 6,1.0 6,-1.6 C6,-5 2.2,-6.2 0,-3.6 Z"
                              fill="#fb7185"
                              stroke="#ffffff"
                              strokeWidth={0.6}
                              vectorEffect="non-scaling-stroke"
                            />
                          </g>
                        );
                      })()}
                  </svg>
                </div>
              )}

              {ringDebug && (
                <div className="absolute left-1/2 top-full mt-3 -translate-x-1/2">
                  <div className="inline-block rounded-md bg-white/90 px-3 py-2 font-mono text-xs text-gray-800 shadow">
                    <div>
                      phaseProgressRatio: {phaseProgressRatio.toFixed(3)}
                    </div>
                    <div>phaseStrokeOffset: {phaseStrokeOffset.toFixed(2)}</div>
                    <div>timeRemaining: {timeRemaining.toFixed(2)}</div>
                    {circleDebugStats && (
                      <>
                        <div className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                          Circle layout
                        </div>
                        <div>
                          container: {circleDebugStats.container.width}x
                          {circleDebugStats.container.height} @(
                          {circleDebugStats.container.left},{' '}
                          {circleDebugStats.container.top}) z-
                          {circleDebugStats.container.zIndex}
                        </div>
                        <div>
                          wrapper: {circleDebugStats.wrapper.width}x
                          {circleDebugStats.wrapper.height} @(
                          {circleDebugStats.wrapper.left},{' '}
                          {circleDebugStats.wrapper.top}) z-
                          {circleDebugStats.wrapper.zIndex}
                        </div>
                        <div>
                          svg: {circleDebugStats.svg.width}x
                          {circleDebugStats.svg.height} @(
                          {circleDebugStats.svg.left},{' '}
                          {circleDebugStats.svg.top}) z-
                          {circleDebugStats.svg.zIndex}
                        </div>
                        {progressCircleDebug && (
                          <>
                            <div className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                              Progress ring
                            </div>
                            <div>
                              totalLength:{' '}
                              {progressCircleDebug.totalLength ?? 'n/a'}
                            </div>
                            <div>
                              stroke:{' '}
                              {progressCircleDebug.strokeColor || 'unknown'} /
                              width {progressCircleDebug.strokeWidth || 'n/a'}
                            </div>
                            <div>
                              dashArray:{' '}
                              {progressCircleDebug.dashArray || 'unset'}
                            </div>
                            <div>
                              dashOffset:{' '}
                              {progressCircleDebug.dashOffset || 'unset'}
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom spacer / controls section */}
        <div className="flex w-full flex-1 flex-col items-center justify-center pb-4 sm:justify-end sm:pb-12">
          <div className="flex items-center">
            <button
              onClick={() => handleUserPauseToggle()}
              disabled={isIntroPlaying}
              aria-label={
                isIntroPlaying ? 'Starting' : isActive ? 'Pause' : 'Start'
              }
              className={`rounded-full px-12 py-4 text-lg font-medium backdrop-blur-sm transition-opacity duration-200 focus:outline-none ${
                controlsFaded ? 'opacity-30' : 'opacity-100'
              } hover:opacity-100 ${isActive ? 'bg-white/20 text-gray-900' : 'bg-white/15 text-gray-900'}`}
              style={{ boxShadow: 'none' }}
            >
              {isIntroPlaying ? 'Starting…' : isActive ? 'Pause' : 'Start'}
            </button>
          </div>
        </div>
      </div>

      {showCompleteModal && (
        <SessionCompleteModal
          isOpen={showCompleteModal}
          isAuthenticated={isAuthenticated}
          onClose={handleModalClose}
          onSavePublic={handleSavePublic}
          onSavePrivate={handleSavePrivate}
          onSignUp={handleSignUpRedirect}
          onSignIn={handleSignInRedirect}
          timeRemaining={publicSaveTimeRemaining}
          isTimeExpired={isPublicSaveExpired}
        />
      )}

      {/* Badge Unlocked Modal */}
      {showBadgeModal && (
        <BadgeUnlockedModal
          isOpen={showBadgeModal}
          badges={unlockedBadges}
          onClose={() => setShowBadgeModal(false)}
        />
      )}

      {/* Pause modal shown on user-initiated Pause */}
      {showPauseModal && (
        <div className="pointer-events-auto fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-6 sm:px-6 sm:py-10">
          <div className="w-full max-w-lg rounded-[32px] bg-[#cffbfe] p-6 shadow-[0_35px_60px_rgba(15,23,42,0.35)] ring-1 ring-white/60 backdrop-blur-xl">
            <div className="text-center text-xs font-semibold uppercase tracking-[0.5em] text-gray-500">
              Paused
            </div>
            <h2 className="mt-2 text-center text-3xl font-semibold leading-tight text-gray-900">
              Session paused
            </h2>
            <p className="mt-3 text-center text-sm leading-relaxed text-gray-600">
              Stay present with the breathing ring. Resume whenever you are
              ready, start over if you want a fresh begin, or exit to the
              dashboard.
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <button
                type="button"
                onClick={handleContinue}
                className="w-full rounded-2xl bg-slate-900/90 px-5 py-3 text-sm font-semibold tracking-wide text-white shadow-lg shadow-slate-900/30 transition hover:bg-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
              >
                Resume Session
              </button>
              <button
                type="button"
                onClick={handleRestart}
                className="w-full rounded-2xl bg-slate-900/90 px-5 py-3 text-sm font-semibold tracking-wide text-white shadow-lg shadow-slate-900/30 transition hover:bg-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
              >
                Restart Session
              </button>
              <button
                type="button"
                onClick={handleQuit}
                className="w-full rounded-2xl bg-slate-900/90 px-5 py-3 text-sm font-semibold tracking-wide text-white shadow-lg shadow-slate-900/30 transition hover:bg-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
              >
                End Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Debug overlays removed per request */}
    </div>
  );
}

export default Breathing;
